/**
 * FidelidApp Widget — storefront widget (banner de puntos, página de recompensas, add-to-cart).
 *
 * ⚠️ ENTREGA: este archivo es la FUENTE de verdad, pero NO se entrega vía `shopify app deploy`
 * (el theme app extension está deprecado). El backend lo sube al theme: `injectWidgetIntoTheme()`
 * en shopify/shopifyService.js lee este archivo del filesystem y lo publica como theme asset.
 * Para que un cambio llegue a la tienda: deployar el BACKEND + disparar POST /api/shopify/sync-widget.
 * Detalle en el CLAUDE.md raíz, sección "Shopify Storefront Widget".
 *
 * App embed unico: detecta paginas, product cards, cart drawer, cuenta y pagina de recompensas.
 * Usa deteccion universal basada en URLs (/products/) en vez de selectores especificos de tema.
 */
(function () {
  "use strict";

  // ─── Config global (se lee del embed element) ───
  var CONFIG = null;

  function getConfig() {
    if (CONFIG) return CONFIG;
    var el = document.querySelector("[data-fidelidapp-embed]");
    if (!el) return null;

    CONFIG = {
      apiBase: el.getAttribute("data-api-base") || "https://api.fidelidapp.cl",
      shop: el.getAttribute("data-shop") || "",
      // Normalizar SIEMPRE: FidelidApp guarda emails en minúsculas; un email con
      // mayúsculas desde Liquid haría que el cliente vea 0 puntos sin explicación.
      email: (el.getAttribute("data-customer-email") || "").trim().toLowerCase(),
      customerName: el.getAttribute("data-customer-name") || "",
      cartTotal: el.getAttribute("data-cart-total") || "0",
      productPrice: el.getAttribute("data-product-price") || "0",
      pageType: el.getAttribute("data-page-type") || "",
      pageHandle: el.getAttribute("data-page-handle") || "",
      currency: el.getAttribute("data-currency") || "",
      enableProduct: el.getAttribute("data-enable-product") === "true",
      enableCart: el.getAttribute("data-enable-cart") === "true",
      enableAccount: el.getAttribute("data-enable-account") === "true",
      enableRewardsPage: el.getAttribute("data-enable-rewards-page") === "true",
      rewardsPageSlug: el.getAttribute("data-rewards-page-slug") || "recompensas",
      textProductBefore: el.getAttribute("data-text-product-before") || "Suma",
      textProductAfter: el.getAttribute("data-text-product-after") || "puntos con este producto",
      textCartBefore: el.getAttribute("data-text-cart-before") || "Con esta compra ganas",
      textCartAfter: el.getAttribute("data-text-cart-after") || "puntos",
      loginUrl: el.getAttribute("data-login-url") || "/account/login",
      pointsPerPurchase: null,
    };
    return CONFIG;
  }

  // Íconos inline (trazo fino, heredan color del texto) — reemplazan emojis
  var FW_ICONS = {
    star: '<svg class="fw-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
    gift: '<svg class="fw-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>',
    check: '<svg class="fw-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>',
    alert: '<svg class="fw-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
  };

  // Ilustraciones para recompensas sin foto de producto (mismo bloque visual
  // que las imágenes: campo de color suave + trazo fino del acento del theme)
  var FW_ART = {
    truck: '<svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 17H5c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h8c.6 0 1 .4 1 1v10z"/><path d="M14 9h3.6c.3 0 .6.1.8.4l2.4 3c.1.2.2.4.2.6v3c0 .6-.4 1-1 1h-1"/><circle cx="7.5" cy="17.5" r="1.8"/><circle cx="16.5" cy="17.5" r="1.8"/><path d="M9.3 17.5h5.4"/><path d="M1 9h1.6M1 12h1.6"/></svg>',
    ticketPercent: '<svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-6z"/><line x1="9" y1="14.5" x2="15" y2="9.5"/><circle cx="9.3" cy="10" r="0.9"/><circle cx="14.7" cy="14" r="0.9"/></svg>',
    ticketAmount: '<svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-6z"/><path d="M12 7.8v8.4"/><path d="M14.2 9.6c-.5-.7-1.2-1-2.2-1-1.3 0-2.2.6-2.2 1.6 0 1 .9 1.4 2.2 1.6 1.3.2 2.4.6 2.4 1.7 0 1-1 1.7-2.4 1.7-1 0-1.8-.3-2.3-1"/></svg>',
    gift: '<svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>',
  };

  function buildRewardArtHtml(rewardType) {
    var icon;
    if (rewardType === 'free_shipping') icon = FW_ART.truck;
    else if (rewardType === 'percentage' || rewardType === 'collection_discount') icon = FW_ART.ticketPercent;
    else if (rewardType === 'fixed_amount') icon = FW_ART.ticketAmount;
    else icon = FW_ART.gift;
    return '<div class="fidelidapp-reward__art" aria-hidden="true">' + icon + '</div>';
  }

  function escapeHtml(str) {
    var div = document.createElement("div");
    div.textContent = str || "";
    return div.innerHTML;
  }

  // Login passwordless de Shopify (new customer accounts): el cliente pone su email,
  // recibe un código de 6 dígitos y vuelve al path indicado con la sesión de
  // storefront creada — sin contraseña y sin registro previo.
  // nota: en tiendas con cuentas clásicas este endpoint no aplica; ahí habría que
  // usar cfg.loginUrl (/account/login). Chetomi usa new customer accounts.
  function getLoginUrl(returnTo) {
    var cfg = getConfig();
    var target = returnTo ||
      (cfg && cfg.enableRewardsPage ? "/pages/" + cfg.rewardsPageSlug : window.location.pathname);
    return "/customer_authentication/login?return_to=" + encodeURIComponent(target);
  }

  function getThemeTokens() {
    var FALLBACK = {
      accent: '#7c3aed', accentText: '#ffffff', accentHover: '',
      fg: '#111827', bg: '#ffffff', font: 'inherit', headingFont: '', radius: '8px',
    };
    try {
      var root = getComputedStyle(document.documentElement);
      // Lee la primera variable con valor usable (saltea "R G B" sueltos de algunos themes)
      var read = function (names) {
        for (var i = 0; i < names.length; i++) {
          var v = root.getPropertyValue(names[i]).trim();
          if (v && !/^\d+ \d+ \d+$/.test(v)) return v;
        }
        return '';
      };

      // Horizon nuevo (V4) publica --color-primary-button-*; themes anteriores --color-button
      var accent = read(['--color-primary-button-background', '--color-button']);
      var accentText = read(['--color-primary-button-text', '--color-button-text']);
      var accentHover = read(['--color-primary-button-hover-background']);
      var fg = read(['--color-foreground']);
      var bg = read(['--color-background']);
      var bodyFont = read(['--font-body-family']) ||
        (document.body ? getComputedStyle(document.body).fontFamily : '');

      // Tipografía de títulos y radius reales del theme, medidos del DOM
      var headingEl = document.querySelector('h1, h2, .h1, .h2');
      var headingFont = headingEl ? getComputedStyle(headingEl).fontFamily : '';
      var radius = '';
      var themeBtn = document.querySelector('button[type="submit"], a.button, .button, .shopify-payment-button__button');
      if (themeBtn) radius = getComputedStyle(themeBtn).borderRadius || '';

      return {
        accent: accent || FALLBACK.accent,
        accentText: accentText || FALLBACK.accentText,
        accentHover: accentHover || '',
        fg: fg || FALLBACK.fg,
        bg: bg || FALLBACK.bg,
        font: bodyFont || FALLBACK.font,
        headingFont: headingFont || '',
        radius: radius || FALLBACK.radius,
      };
    } catch (e) {
      return FALLBACK;
    }
  }

  function applyThemeTokens() {
    var t = getThemeTokens();
    var rootStyle = document.documentElement.style;
    rootStyle.setProperty('--fw-accent', t.accent);
    rootStyle.setProperty('--fw-accent-text', t.accentText);
    rootStyle.setProperty('--fw-accent-hover', t.accentHover || t.accent);
    rootStyle.setProperty('--fw-fg', t.fg);
    rootStyle.setProperty('--fw-bg', t.bg);
    rootStyle.setProperty('--fw-radius', t.radius);
    rootStyle.setProperty('--fw-font', t.font);
    rootStyle.setProperty('--fw-font-heading', t.headingFont || t.font);
    // Compat: variables viejas apuntan al acento (usadas por CSS previo del widget)
    rootStyle.setProperty('--fw-btn-bg', t.accent);
    rootStyle.setProperty('--fw-btn-text', t.accentText);

    // Derivados con alpha (bordes y fondos suaves): el color del theme puede venir
    // en cualquier formato — lo resolvemos a RGB con un probe y computed style.
    var toRgb = function (color) {
      try {
        var probe = document.createElement('span');
        probe.style.color = color;
        probe.style.display = 'none';
        document.body.appendChild(probe);
        var out = getComputedStyle(probe).color;
        document.body.removeChild(probe);
        var m = out.match(/(\d+),?\s*(\d+),?\s*(\d+)/);
        return m ? m[1] + ', ' + m[2] + ', ' + m[3] : null;
      } catch (e) { return null; }
    };
    var fgRgb = toRgb(t.fg) || '17, 24, 39';
    var accentRgb = toRgb(t.accent) || '124, 58, 237';
    rootStyle.setProperty('--fw-border', 'rgba(' + fgRgb + ', 0.16)');
    rootStyle.setProperty('--fw-border-soft', 'rgba(' + fgRgb + ', 0.08)');
    rootStyle.setProperty('--fw-muted', 'rgba(' + fgRgb + ', 0.55)');
    rootStyle.setProperty('--fw-accent-soft', 'rgba(' + accentRgb + ', 0.07)');
    rootStyle.setProperty('--fw-accent-border', 'rgba(' + accentRgb + ', 0.35)');
  }

  // ─── API helpers ───

  function apiGet(path) {
    var cfg = getConfig();
    return fetch(cfg.apiBase + path).then(function (r) {
      return r.ok ? r.json() : Promise.reject(r.status);
    });
  }

  function apiPost(path, body) {
    var cfg = getConfig();
    return fetch(cfg.apiBase + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (data) {
        return { ok: r.ok, data: data };
      });
    });
  }

  // ─── Cache de pointsPerPurchase y de points del usuario ───

  // Promise cacheada: evita que initPointsToast() e initHeaderBadge() hagan
  // dos llamadas idénticas a /points en la misma carga de página.
  var _pointsPromise = null;
  var _cartBannerInjecting = false;
  // Container de la página de recompensas (para re-render tras un canje exitoso)
  var _rewardsPageEl = null;
  // Lock para inyección en drawer — variable JS, no atributo DOM (morphdom puede borrar atributos)
  var _drawerBannerInjecting = false;
  function getPointsData(cfg) {
    if (!_pointsPromise) {
      _pointsPromise = apiGet(
        "/api/shopify/public/points?shop=" + encodeURIComponent(cfg.shop) +
        "&email=" + encodeURIComponent(cfg.email)
      );
    }
    return _pointsPromise;
  }

  async function getPointsPerPurchase() {
    var cfg = getConfig();
    if (!cfg) return 1;
    if (cfg.pointsPerPurchase !== null) return cfg.pointsPerPurchase;

    try {
      var data = await apiGet(
        "/api/shopify/public/estimate?shop=" + encodeURIComponent(cfg.shop) + "&total=1"
      );
      cfg.pointsPerPurchase = data.pointsPerPurchase || 1;
      return cfg.pointsPerPurchase;
    } catch (e) { }
    cfg.pointsPerPurchase = 1;
    return 1;
  }

  // ─── Extraer precio de texto ───

  function extractPrice(text) {
    var cleaned = text.replace(/[^\d.,]/g, "");
    if (!cleaned) return 0;

    var dotCount = (cleaned.match(/\./g) || []).length;
    var commaCount = (cleaned.match(/,/g) || []).length;

    if (dotCount === 1 && commaCount === 0) {
      var afterDot = cleaned.split(".")[1];
      if (afterDot && afterDot.length === 3) {
        cleaned = cleaned.replace(".", "");
      }
    } else if (dotCount > 1) {
      cleaned = cleaned.replace(/\./g, "");
    }

    if (commaCount === 1 && dotCount === 0) {
      var afterComma = cleaned.split(",")[1];
      if (afterComma && afterComma.length <= 2) {
        cleaned = cleaned.replace(",", ".");
      } else {
        cleaned = cleaned.replace(",", "");
      }
    } else if (commaCount > 1) {
      cleaned = cleaned.replace(/,/g, "");
    }

    // Formato mixto: 10.000,50 o 10,000.50
    if (dotCount >= 1 && commaCount >= 1) {
      var lastDot = cleaned.lastIndexOf(".");
      var lastComma = cleaned.lastIndexOf(",");
      if (lastComma > lastDot) {
        cleaned = cleaned.replace(/\./g, "").replace(",", ".");
      } else {
        cleaned = cleaned.replace(/,/g, "");
      }
    }

    return parseFloat(cleaned) || 0;
  }

  // ─── Badge de puntos (inline) ───

  function createPointsBadge(points) {
    var cfg = getConfig();
    var badge = document.createElement("div");
    badge.className = "fidelidapp-points-badge";
    badge.setAttribute("data-fidelidapp-injected", "true");
    badge.innerHTML = FW_ICONS.star +
      '<span>' + escapeHtml(cfg ? cfg.textProductBefore + ' ' : 'Suma ') +
      '<strong>' + points.toLocaleString() + ' pts.</strong> con este producto</span>';
    return badge;
  }

  // ════════════════════════════════════════════════════
  // 1. PRODUCTO: banner en pagina de producto
  // ════════════════════════════════════════════════════

  // Páginas de "armá tu combo" (builder custom): el precio se muestra en .combo-price__now
  // y cambia con el toggle de tamaño, sin un product form estándar. Inyectamos el banner
  // de puntos junto al botón del combo y lo actualizamos cuando cambia el precio.
  async function initComboPage() {
    var cfg = getConfig();
    if (!cfg || !cfg.enableProduct) return false;
    var builder = document.querySelector(".combo-builder");
    var priceEl = builder && builder.querySelector(".combo-price__now");
    var actions = builder && builder.querySelector(".combo-actions");
    if (!builder || !priceEl || !actions) return false;
    if (document.querySelector("[data-fidelidapp-product-banner]")) return true;

    var ppp = await getPointsPerPurchase();

    var banner = document.createElement("div");
    banner.className = "fidelidapp-product-points notranslate";
    banner.setAttribute("translate", "no");
    banner.setAttribute("data-fidelidapp-injected", "true");
    banner.setAttribute("data-fidelidapp-product-banner", "true");

    function parseComboPrice(txt) {
      return parseFloat(String(txt || "").replace(/[^0-9]/g, "")) || 0;
    }
    function updateComboBanner() {
      var points = Math.round(parseComboPrice(priceEl.textContent) * ppp);
      if (points <= 0) { banner.style.display = "none"; return; }
      banner.style.display = "";
      banner.innerHTML =
        '<span class="fidelidapp-product-points__icon">' + FW_ICONS.star + '</span>' +
        '<span class="fidelidapp-product-points__text">' +
        escapeHtml(cfg.textProductBefore) +
        ' <strong>' + points.toLocaleString() + '</strong> ' +
        escapeHtml(cfg.textProductAfter) +
        '</span>';
    }
    updateComboBanner();
    actions.parentNode.insertBefore(banner, actions);

    // Actualizar los puntos cuando cambia el precio (toggle 6/12 u otra selección)
    try {
      var obs = new MutationObserver(updateComboBanner);
      obs.observe(priceEl, { childList: true, characterData: true, subtree: true });
    } catch (e) { /* sin observer: el banner queda con el precio inicial */ }
    return true;
  }

  async function initProductPage() {
    var cfg = getConfig();
    if (!cfg || !cfg.enableProduct) return;
    if (cfg.pageType !== "product") return;
    // Las páginas de combo tienen su propio handler (precio dinámico, sin product form)
    if (document.querySelector(".combo-builder")) return;

    var initialPrice = parseFloat(cfg.productPrice);
    if (!initialPrice || initialPrice <= 0) return;

    // Obtener pointsPerPurchase una vez
    var ppp = await getPointsPerPurchase();

    // Crear banner
    var banner = document.createElement("div");
    banner.className = "fidelidapp-product-points notranslate";
    banner.setAttribute("translate", "no");
    banner.setAttribute("data-fidelidapp-injected", "true");
    banner.setAttribute("data-fidelidapp-product-banner", "true");

    function updateBanner(currentPrice) {
      var points = Math.round(currentPrice * ppp);
      if (points <= 0) {
        banner.style.display = "none";
        return;
      }
      banner.style.display = "";
      banner.innerHTML =
        '<span class="fidelidapp-product-points__icon">' + FW_ICONS.star + '</span>' +
        '<span class="fidelidapp-product-points__text">' +
        escapeHtml(cfg.textProductBefore) +
        ' <strong>' + points.toLocaleString() + '</strong> ' +
        escapeHtml(cfg.textProductAfter) +
        '</span>';
    }

    updateBanner(initialPrice);

    // Inyectar el banner
    var injected = false;
    var targets = [
      'form[action*="/cart/add"] button[type="submit"]',
      'button[name="add"]',
      '.product-form__submit',
      '[data-add-to-cart]',
      '.shopify-payment-button',
      '.product-form__buttons',
      '.product-form',
      '.product__info-wrapper form',
      '.product-single__form',
    ];

    for (var i = 0; i < targets.length; i++) {
      var el = document.querySelector(targets[i]);
      if (el) {
        var parent = el.closest("form") || el.parentElement;
        if (parent && parent.parentNode) {
          parent.parentNode.insertBefore(banner, parent.nextSibling);
          injected = true;
          break;
        }
      }
    }

    if (!injected) {
      var priceEl = document.querySelector('.product__price, .price, [data-product-price]');
      if (priceEl && priceEl.parentNode) {
        priceEl.parentNode.insertBefore(banner, priceEl.nextSibling);
      }
    }

    // ─── Deteccion de cambio de variante ───
    // Cargar el JSON del producto con todos los precios de variantes
    var variantPriceMap = {};
    try {
      var handle = window.location.pathname.split("/products/")[1];
      if (handle) {
        handle = handle.split("?")[0].split("#")[0].replace(/\/$/, "");
        var productData = await fetch("/products/" + handle + ".js").then(function (r) { return r.json(); });
        if (productData && productData.variants) {
          for (var vi = 0; vi < productData.variants.length; vi++) {
            var v = productData.variants[vi];
            // Shopify .js endpoint devuelve precio en centavos
            variantPriceMap[v.id.toString()] = v.price / 100;
          }
          console.log("[FidelidApp] Loaded", Object.keys(variantPriceMap).length, "variant prices");
        }
      }
    } catch (e) {
      console.warn("[FidelidApp] Could not load product JSON:", e);
    }

    // Funcion que busca el variant ID actual y actualiza el banner
    function onVariantChange() {
      var variantId = getSelectedVariantId();
      if (variantId && variantPriceMap[variantId] !== undefined) {
        updateBanner(variantPriceMap[variantId]);
      }
    }

    // 1. Escuchar cambio en URL (?variant=XXXXX) — todos los temas modernos
    var lastHref = window.location.href;
    var variantPollInterval = setInterval(function () {
      if (window.location.pathname.indexOf("/products/") === -1) {
        clearInterval(variantPollInterval);
        return;
      }
      if (window.location.href !== lastHref) {
        lastHref = window.location.href;
        onVariantChange();
      }
    }, 250);

    // 2. Escuchar evento nativo de Shopify (Dawn y temas Shopify 2.0)
    document.addEventListener("variant:change", function (e) {
      if (e.detail && e.detail.variant && e.detail.variant.price != null) {
        updateBanner(e.detail.variant.price / 100);
      }
    });

    // 3. Escuchar cambios en inputs/selects de variante
    document.addEventListener("change", function (e) {
      var target = e.target;
      if (!target) return;
      var isVariantInput =
        target.name === "id" ||
        target.closest(".product-form__input, .variant-input, [data-option-index], .single-option-selector, .product-form__variants, fieldset.product-form__input");
      if (isVariantInput) {
        // Delay para que el tema actualice el hidden input de variant ID
        setTimeout(onVariantChange, 100);
      }
    });

    // 4. Escuchar clicks en swatches/botones de variante (no siempre disparan "change")
    document.addEventListener("click", function (e) {
      var target = e.target;
      if (!target) return;
      if (target.closest(".product-form__input label, .variant-input label, .swatch label, [data-option-value], .product-form__input .button, .variant__button-label")) {
        setTimeout(onVariantChange, 150);
      }
    });
  }

  /**
   * Obtiene el variant ID seleccionado actualmente
   */
  function getSelectedVariantId() {
    // 1. Desde la URL (?variant=XXXXX) — metodo mas confiable
    var urlParams = new URLSearchParams(window.location.search);
    var fromUrl = urlParams.get("variant");
    if (fromUrl) return fromUrl;

    // 2. Desde el input hidden del form
    var hiddenInput = document.querySelector(
      'form[action*="/cart/add"] input[name="id"], ' +
      'form[action*="/cart/add"] select[name="id"], ' +
      '.product-form input[name="id"], ' +
      '.product-form select[name="id"]'
    );
    if (hiddenInput) return hiddenInput.value;

    return null;
  }

  // ════════════════════════════════════════════════════
  // 2. PRODUCT CARDS: puntos en cualquier tarjeta
  //    Deteccion UNIVERSAL: busca <a href="/products/..."> y sube al container
  // ════════════════════════════════════════════════════

  async function initProductCards() {
    var cfg = getConfig();
    if (!cfg || !cfg.enableProduct) return;

    var ppp = await getPointsPerPurchase();
    console.log("[FidelidApp] initProductCards - pointsPerPurchase:", ppp, "pageType:", cfg.pageType);
    processProductCards(ppp);

    // Observer para contenido dinamico (infinite scroll, filtros, quick view)
    // Se desconecta antes de procesar para no reaccionar a las propias inyecciones.
    var debounceTimer;
    var observer = new MutationObserver(function (mutations) {
      // Ignorar mutaciones causadas por las propias inyecciones de FidelidApp
      var selfCaused = mutations.every(function (m) {
        return Array.from(m.addedNodes).every(function (n) {
          return n.nodeType !== 1 ||
                 n.hasAttribute("data-fidelidapp-injected") ||
                 n.hasAttribute("data-fidelidapp-card-id");
        });
      });
      if (selfCaused) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        observer.disconnect();
        processProductCards(ppp);
        observer.observe(document.body, { childList: true, subtree: true });
      }, 200);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function processProductCards(pointsPerPurchase) {
    var cfg = getConfig();

    // ESTRATEGIA UNIVERSAL: encontrar todos los links a /products/ y trabajar desde ahi
    var productLinks = document.querySelectorAll('a[href*="/products/"]');
    var processedContainers = new Set();

    console.log("[FidelidApp] processProductCards - found", productLinks.length, "product links");

    for (var i = 0; i < productLinks.length; i++) {
      var link = productLinks[i];

      // Ignorar links de navegacion, breadcrumbs, footer, cart drawers, cart pages, buscador y contenido de blog
      if (link.closest("nav, header, footer, .breadcrumb, .breadcrumbs, .pagination")) continue;
      if (link.closest("cart-drawer, .cart-drawer, #CartDrawer, .drawer--cart, [data-cart-drawer], .mini-cart, .side-cart, .cart-notification, form[action='/cart'], .cart, .cart-items, cart-items, table.cart-items")) continue;
      if (link.closest("predictive-search, .predictive-search, #predictive-search, [data-predictive-search], .search-modal, .search-popover, [data-search-results]")) continue;
      if (link.closest("article.article, .article__content, .blog-post__content, .article-template, .blog-article")) continue;

      // Encontrar el contenedor de la card subiendo por el DOM
      var card = findCardContainer(link);
      if (!card) {
        // Fallback: si no encuentra card container, usar el padre directo del link
        // Esto cubre temas minimalistas donde la card no tiene clases especiales
        card = link.parentElement;
        // Subir un nivel mas si el padre es muy pequeno (solo contiene el link)
        if (card && card.children.length <= 1 && card.parentElement) {
          card = card.parentElement;
        }
        console.log("[FidelidApp] Card container not found for link", link.href, "- using fallback parent:", card?.tagName, card?.className);
      }
      if (!card) continue;

      // No procesar el mismo container dos veces
      var cardId = card.getAttribute("data-fidelidapp-card-id");
      if (!cardId) {
        cardId = "fc-" + i;
        card.setAttribute("data-fidelidapp-card-id", cardId);
      }
      if (processedContainers.has(cardId)) continue;
      processedContainers.add(cardId);

      // No inyectar dos veces
      if (card.querySelector("[data-fidelidapp-injected]")) continue;

      // Evitar el producto principal en la pagina de producto
      if (cfg && cfg.pageType === "product") {
        if (card.closest('.product, .product-single, .product__info-wrapper, .product__info-container, [data-product-form], form[action*="/cart/add"]')) continue;
      }

      // Buscar precio dentro de la card
      var priceNum = extractPriceFromCard(card);
      if (!priceNum || priceNum <= 0) {
        console.log("[FidelidApp] No price found in card:", card.tagName, card.className, "- innerHTML preview:", card.innerHTML.substring(0, 200));
        continue;
      }

      var points = Math.round(priceNum * pointsPerPurchase);
      if (points <= 0) continue;

      // Encontrar donde inyectar el badge (despues del precio)
      var priceContainer = findPriceContainer(card);
      if (!priceContainer) {
        // Fallback: inyectar al final de la card
        var badge = createPointsBadge(points);
        card.appendChild(badge);
        console.log("[FidelidApp] Injected badge at end of card (fallback), price:", priceNum, "points:", points);
      } else if (priceContainer.parentNode) {
        var badge = createPointsBadge(points);
        priceContainer.parentNode.insertBefore(badge, priceContainer.nextSibling);
      }
    }
  }

  function findCardContainer(link) {
    // Subir por el DOM buscando un container que parezca una card de producto
    // Los temas usan diferentes clases pero la estructura es similar:
    // <li|div|article class="...card|product|grid-item..."> > ... > <a href="/products/...">
    var el = link.parentElement;
    var depth = 0;
    var candidate = null;

    while (el && el !== document.body && depth < 10) {
      var tag = el.tagName.toLowerCase();
      var cls = (el.className || "").toLowerCase();

      // Saltar sub-elementos BEM de cards (card__heading, card__media, card__content, etc.)
      // Solo matchear "card" si NO es un BEM child element (contiene __ despues de card)
      var isCardSubElement = /card__/.test(cls);
      var isCardContainer = !isCardSubElement && (
        /\bcard\b/.test(cls) ||
        /card-wrapper/.test(cls) ||
        /product-card/.test(cls)
      );

      // Buscar contenedores tipicos
      if (
        tag === "li" ||
        tag === "article" ||
        isCardContainer ||
        cls.indexOf("product-item") !== -1 ||
        cls.indexOf("grid-item") !== -1 ||
        cls.indexOf("grid__item") !== -1 ||
        cls.indexOf("collection-product") !== -1 ||
        el.hasAttribute("data-product-id") ||
        el.hasAttribute("data-product-card")
      ) {
        candidate = el;
        // Para <li> y grid__item seguir subiendo un nivel mas por si hay un wrapper
        // Para otros, aceptar directamente
        if (tag !== "li" && cls.indexOf("grid__item") === -1) {
          break;
        }
        // Si ya es un li o grid__item, es un buen candidato pero seguimos
        // por si hay un nivel superior mas apropiado
        break;
      }

      // Si encontramos un grid/list container, el hijo anterior era la card
      if (
        (cls.indexOf("grid") !== -1 && cls.indexOf("grid-item") === -1 && cls.indexOf("grid__item") === -1) ||
        (cls.indexOf("collection") !== -1 && cls.indexOf("collection-product") === -1) ||
        cls.indexOf("product-grid") !== -1 ||
        cls.indexOf("product-list") !== -1
      ) {
        break;
      }

      el = el.parentElement;
      depth++;
    }

    return candidate;
  }

  function extractPriceFromCard(card) {
    // Priorizar precio de oferta sobre precio regular
    var saleSelectors = [
      ".price-item--sale",
      ".price--sale .money",
      ".price-item--sale .money",
      ".price--on-sale .money",
      "s + .money",       // Estructura comun: <s>precio viejo</s> <span class="money">precio nuevo</span>
      "ins .money",
      ".price--reduced",
      "[data-sale-price]",
    ];

    for (var s = 0; s < saleSelectors.length; s++) {
      var saleEl = card.querySelector(saleSelectors[s]);
      if (saleEl) {
        var salePrice = extractPrice(saleEl.textContent);
        if (salePrice > 0) return salePrice;
      }
    }

    // Precio regular — incluye selectores de Dawn theme
    var priceSelectors = [
      ".price-item--regular",
      ".price-item",
      ".price .money",
      "span.money",
      ".product-price",
      ".card__price",
      ".card-information .price",
      ".card__information .price",
      ".card-information .price-item",
      ".card__information .price-item",
      ".grid-product__price",
      "[data-product-price]",
      ".price__regular .price-item",
      ".price__container .price-item",
      ".price-item--last .money",
    ];

    for (var p = 0; p < priceSelectors.length; p++) {
      var priceEl = card.querySelector(priceSelectors[p]);
      if (priceEl) {
        // Si este precio esta tachado (dentro de <s>, <del>, o clase compare), buscar otro
        if (priceEl.closest("s, del, .price--compare, .price-item--compare, .price__compare")) continue;
        var price = extractPrice(priceEl.textContent);
        if (price > 0) return price;
      }
    }

    // Ultimo recurso: buscar cualquier elemento que contenga un simbolo de moneda
    var allElements = card.querySelectorAll("span, div, p");
    for (var e = 0; e < allElements.length; e++) {
      var text = allElements[e].textContent.trim();
      // Solo elementos con texto corto que parezca un precio
      if (text.length > 20) continue;
      if (/[\$\€\£]/.test(text) || /\d+[.,]\d{2,3}/.test(text)) {
        if (allElements[e].closest("s, del")) continue;
        var fallbackPrice = extractPrice(text);
        if (fallbackPrice > 0) return fallbackPrice;
      }
    }

    return 0;
  }

  function findPriceContainer(card) {
    // Buscar el contenedor de precio mas especifico
    var containers = [
      ".price",
      ".card-information .price",
      ".card__information .price",
      ".grid-product__price",
      ".product-price",
      ".card__price",
      "[data-product-price]",
      ".price__container",
      ".card-information .price-item",
      ".card__information .price-item",
    ];

    for (var c = 0; c < containers.length; c++) {
      var el = card.querySelector(containers[c]);
      if (el) return el;
    }

    // Fallback: el elemento de precio directo
    var priceEl = card.querySelector(".price-item, .money, span.money");
    if (priceEl) return priceEl.parentElement || priceEl;

    return null;
  }

  // ════════════════════════════════════════════════════
  // 3. CARRITO: pagina y drawer
  // ════════════════════════════════════════════════════

  async function initCart() {
    var cfg = getConfig();
    console.log("[FidelidApp] initCart - enableCart:", cfg?.enableCart, "pageType:", cfg?.pageType);
    if (!cfg || !cfg.enableCart) return;

    // Pagina de carrito
    if (cfg.pageType === "cart") {
      await injectCartBanner(parseFloat(cfg.cartTotal));

      // Actualizar puntos si el usuario cambia cantidades sin recargar la página
      var cartPageTotalSelectors = [
        ".totals__subtotal-value", ".totals__total-value",
        ".cart__total .money", ".cart__subtotal", "[data-cart-subtotal]",
      ];
      var cartPageTotalEl = null;
      for (var cpt = 0; cpt < cartPageTotalSelectors.length; cpt++) {
        cartPageTotalEl = document.querySelector(cartPageTotalSelectors[cpt]);
        if (cartPageTotalEl) break;
      }
      if (cartPageTotalEl) {
        var debounceCartPage;
        var lastCartPageTotal = cartPageTotalEl.textContent;
        new MutationObserver(function () {
          var newText = cartPageTotalEl.textContent;
          if (newText === lastCartPageTotal) return;
          lastCartPageTotal = newText;
          clearTimeout(debounceCartPage);
          debounceCartPage = setTimeout(function () {
            var newTotal = extractPrice(newText);
            if (newTotal <= 0) return;
            var existing = document.querySelector("[data-fidelidapp-cart-injected]");
            if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
            injectCartBanner(newTotal);
          }, 300);
        }).observe(cartPageTotalEl, { childList: true, characterData: true, subtree: true });
      }
    }

    // Observer para drawer (detecta cambios en DOM, atributos de visibilidad)
    var debounceDrawer;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceDrawer);
      debounceDrawer = setTimeout(tryInjectDrawerBanner, 150);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "open", "aria-hidden", "style", "data-active"],
    });

    // Click en iconos de carrito — amplio para todos los temas
    document.addEventListener("click", function (e) {
      var target = e.target.closest(
        'a[href="/cart"], [data-cart-toggle], .cart-icon, .cart-link, ' +
        '.header__icon--cart, #cart-icon-bubble, .cart-count-bubble, ' +
        'cart-toggle, .js-cart-toggle, [data-action="toggle-cart"], ' +
        '.site-header__cart, .cart-button, [aria-controls*="cart"]'
      );
      if (target) {
        console.log("[FidelidApp] Cart icon clicked, scheduling drawer injection attempts");
        setTimeout(tryInjectDrawerBanner, 300);
        setTimeout(tryInjectDrawerBanner, 800);
        setTimeout(tryInjectDrawerBanner, 1500);
      }
    });

    tryInjectDrawerBanner();
  }

  function tryInjectDrawerBanner() {
    // En la página /cart, injectCartBanner ya manejó la inyección — no duplicar
    var _cfg = getConfig();
    if (_cfg && _cfg.pageType === 'cart') return;

    // Buscar drawers de carrito con selectores amplios
    var drawerSelectors = [
      "dialog[open]",              // HTML dialog estándar (e.g. tema Che Tomi)
      "cart-drawer-component",     // custom element wrapper (Che Tomi)
      "cart-drawer",
      "cart-notification",
      ".cart-drawer",
      "#cart-drawer",
      "#CartDrawer",
      ".drawer--cart",
      "[data-cart-drawer]",
      ".mini-cart",
      ".side-cart",
      ".ajaxcart",
      ".cart-notification",
      "#shopify-section-cart-drawer",
      "[data-section-type='cart-drawer']",
      ".cart-flyout",
      ".slide-cart",
    ];

    // Buscar todos los drawers pero trackear los que ya procesamos (pueden ser el mismo nodo con distintos selectores)
    var foundAny = false;
    var processedNodes = new Set();
    for (var i = 0; i < drawerSelectors.length; i++) {
      var drawers = document.querySelectorAll(drawerSelectors[i]);
      for (var j = 0; j < drawers.length; j++) {
        var drawer = drawers[j];
        foundAny = true;

        // Evitar procesar el mismo nodo DOM dos veces (cart-drawer, .cart-drawer, #CartDrawer pueden ser el mismo)
        if (processedNodes.has(drawer)) continue;
        processedNodes.add(drawer);

        // Para dialog[open] verificar que sea realmente un carrito y no un Quick View u otro modal
        if (drawer.tagName === "DIALOG") {
          var isCartDialog =
            drawer.querySelector("cart-items, [data-cart-items], .cart-items, .cart-drawer__cart-items") ||
            (drawer.id || "").toLowerCase().indexOf("cart") !== -1 ||
            (drawer.className || "").toLowerCase().indexOf("cart") !== -1;
          if (!isCartDialog) continue;
        }

        if (drawer.querySelector("[data-fidelidapp-cart-injected]") ||
            _drawerBannerInjecting) {
          continue;
        }

        var visible = isElementVisible(drawer);
        if (!visible) {
          continue;
        }

        // Solo loguear una vez por selector para no spamear
        if (!drawer._fidelidappLogged) {
          drawer._fidelidappLogged = true;
          console.log("[FidelidApp] Cart drawer found and visible:", drawerSelectors[i], "tag:", drawer.tagName);

          // Dump de clases y texto del drawer para debug
          var allEls = drawer.querySelectorAll("*");
          var classSet = new Set();
          var moneyTexts = [];
          for (var d = 0; d < allEls.length && d < 300; d++) {
            var cn = allEls[d].className;
            if (cn && typeof cn === "string") {
              cn.split(" ").forEach(function(c) { if (c.trim()) classSet.add(c.trim()); });
            }
            // Buscar elementos con texto que parezca precio
            var txt = (allEls[d].textContent || "").trim();
            if (txt.length < 30 && /[\$\€]|subtotal|total/i.test(txt)) {
              moneyTexts.push(allEls[d].tagName + "." + (allEls[d].className || "") + " = " + txt);
            }
          }
          console.log("[FidelidApp] Drawer classes:", Array.from(classSet).sort().join(", "));
          console.log("[FidelidApp] Drawer money/total texts:", moneyTexts);
        }

        // Buscar total en el drawer
        var total = extractCartTotal(drawer);
        if (total > 0) {
          console.log("[FidelidApp] Cart drawer total:", total);
          _drawerBannerInjecting = true;
          injectCartBannerInto(drawer, total);
          return;
        }
      }
    }

    if (!foundAny) {
      console.log("[FidelidApp] tryInjectDrawerBanner: no drawer elements found in DOM at all");
    }
  }

  function isElementVisible(el) {
    // Custom elements (como <cart-drawer>) son visibles si tienen open o clase activa
    var tag = (el.tagName || "").toLowerCase();
    if (tag.indexOf("cart") !== -1) {
      if (el.hasAttribute("open")) return true;
      var cls = (el.className || "").toLowerCase();
      if (cls.indexOf("active") !== -1 || cls.indexOf("is-open") !== -1 || cls.indexOf("drawer--is-open") !== -1) return true;
    }

    // Atributo open (como <details open>)
    if (el.hasAttribute("open")) return true;

    var style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;

    // Si tiene transform translateX grande, esta fuera de pantalla
    var transform = style.transform;
    if (transform && transform !== "none") {
      var match = transform.match(/translateX\((-?\d+)/);
      if (match && Math.abs(parseInt(match[1])) > 100) return false;
    }

    return true;
  }

  function extractCartTotal(container) {
    var selectors = [
      ".totals__subtotal-value",
      ".totals__total-value",
      ".cart-totals__total-value",   // Che Tomi y temas con cart-totals
      ".cart-drawer__subtotal .money",
      ".cart-drawer__total .money",
      ".cart__total .money",
      ".totals .money",
      "[data-cart-subtotal]",
      ".mini-cart__total .money",
      ".cart-subtotal .money",
      ".drawer__subtotal .money",
      ".cart-notification__total .money",
      ".cart__subtotal",
      "[data-cart-total]",
    ];

    for (var k = 0; k < selectors.length; k++) {
      var totalEl = container.querySelector(selectors[k]);
      if (totalEl) {
        var total = extractPrice(totalEl.textContent);
        if (total > 0) return total;
      }
    }

    // Fallback: buscar el ultimo span.money dentro del container
    var moneyEls = container.querySelectorAll("span.money, .money");
    if (moneyEls.length > 0) {
      var lastMoney = moneyEls[moneyEls.length - 1];
      var total = extractPrice(lastMoney.textContent);
      if (total > 0) return total;
    }

    return 0;
  }

  async function injectCartBanner(total) {
    if (!total || total <= 0) return;
    if (document.querySelector("[data-fidelidapp-cart-injected]")) return;
    if (_cartBannerInjecting) return;
    _cartBannerInjecting = true;
    var cfg = getConfig();

    try {
      var data = await apiGet(
        "/api/shopify/public/estimate?shop=" + encodeURIComponent(cfg.shop) + "&total=" + total
      );
      if (!data.estimatedPoints || data.estimatedPoints <= 0) return;

      // Re-verificar después del await: otra llamada concurrente pudo haber terminado primero
      if (document.querySelector("[data-fidelidapp-cart-injected]")) return;

      var banner = buildCartBannerElement(data.estimatedPoints);

      // Encontrar el bloque de totales y poner el banner debajo
      var totalIndicators = [
        ".totals__subtotal-value",
        ".totals__total-value",
        ".cart__total",
        ".cart__subtotal",
        "[data-cart-subtotal]",
      ];

      for (var s = 0; s < totalIndicators.length; s++) {
        var totalEl = document.querySelector(totalIndicators[s]);
        if (!totalEl) continue;

        var totalsWrapper = totalEl.closest(
          ".totals, .cart__footer, .cart__totals, .cart-footer"
        );
        if (!totalsWrapper) totalsWrapper = totalEl.parentElement;

        if (totalsWrapper && totalsWrapper.parentNode) {
          totalsWrapper.parentNode.insertBefore(banner, totalsWrapper.nextSibling);
          return;
        }
      }

      // Fallback: al final del form de carrito
      var targets = [
        'form[action="/cart"]',
        '.cart',
        '.cart-form',
        '[data-cart-form]',
        'main .cart',
        '#MainContent form',
        '.cart__content',
        '.cart-page',
      ];

      for (var i = 0; i < targets.length; i++) {
        var el = document.querySelector(targets[i]);
        if (el) {
          el.appendChild(banner);
          return;
        }
      }
    } catch (e) {
      console.warn("[FidelidApp] Error en carrito:", e);
    } finally {
      _cartBannerInjecting = false;
    }
  }

  async function injectCartBannerInto(drawer, total) {
    var cfg = getConfig();
    if (!cfg) {
      _drawerBannerInjecting = false;
      return;
    }

    try {
      var data = await apiGet(
        "/api/shopify/public/estimate?shop=" + encodeURIComponent(cfg.shop) + "&total=" + total
      );
      if (!data.estimatedPoints || data.estimatedPoints <= 0) {
        return;
      }

      // Limpiar banners previos en cualquier parte del documento antes de insertar
      document.querySelectorAll("[data-fidelidapp-cart-injected]").forEach(function(el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });

      var wrapper = document.createElement("div");
      wrapper.setAttribute("data-fidelidapp-cart-injected", "true");
      wrapper.appendChild(buildCartBannerElement(data.estimatedPoints));

      // Estrategia: encontrar el contenedor de totales y poner el banner DEBAJO de todo el bloque
      // Buscamos un elemento de subtotal/total, subimos al wrapper que contiene TODAS las lineas de totales,
      // y ponemos el banner despues de ese wrapper.

      var totalIndicators = [
        ".totals__subtotal-value",
        ".totals__total-value",
        ".cart-drawer__subtotal",
        ".cart-drawer__total",
        ".cart__total",
        ".cart__subtotal",
        "[data-cart-subtotal]",
        "[data-cart-total]",
      ];

      var injected = false;

      for (var s = 0; s < totalIndicators.length; s++) {
        var totalEl = drawer.querySelector(totalIndicators[s]);
        if (!totalEl) continue;

        // Subir al contenedor que envuelve todas las lineas de totales
        // Buscar ancestros con clases como "totals", "footer", "cart-drawer__footer"
        var totalsWrapper = totalEl.closest(
          ".totals, .cart-totals, .cart-drawer__summary, " +
          ".cart-drawer__footer, .drawer__footer, .cart__footer, " +
          ".mini-cart__footer, .cart-notification__footer, .cart__totals, " +
          ".cart-drawer__totals, .drawer__totals"
        );

        if (!totalsWrapper) {
          // Si no hay wrapper con clase conocida, usar el padre del elemento de total
          totalsWrapper = totalEl.parentElement;
        }

        if (totalsWrapper && totalsWrapper.parentNode) {
          // Insertar el banner DESPUES del wrapper de totales completo
          totalsWrapper.parentNode.insertBefore(wrapper, totalsWrapper.nextSibling);
          injected = true;
          break;
        }
      }

      if (!injected) {
        // Debug: listar todos los elementos del drawer para ver la estructura
        console.log("[FidelidApp] Cart drawer: no subtotal found. Drawer classes:", drawer.className, "Drawer tag:", drawer.tagName);
        var allChildren = drawer.querySelectorAll("*");
        var classes = new Set();
        for (var c = 0; c < allChildren.length && c < 200; c++) {
          if (allChildren[c].className && typeof allChildren[c].className === "string") {
            allChildren[c].className.split(" ").forEach(function (cls) {
              if (cls.trim()) classes.add(cls.trim());
            });
          }
        }
        console.log("[FidelidApp] Cart drawer all classes:", Array.from(classes).join(", "));

        // Fallback: al final del drawer content
        var content = drawer.querySelector(
          '.cart-drawer__inner, .drawer__inner, .mini-cart__content, ' +
          '.cart-drawer__body, .cart-drawer__main, .drawer__content'
        ) || drawer;
        content.appendChild(wrapper);
      }
    } catch (e) {
      console.warn("[FidelidApp] Error en cart drawer:", e);
    } finally {
      // Liberar el lock JS siempre (reemplaza el atributo DOM que morphdom podía borrar)
      _drawerBannerInjecting = false;
    }
  }

  function buildCartBannerElement(points) {
    var cfg = getConfig();
    var banner = document.createElement("div");
    banner.className = "fidelidapp-banner notranslate";
    banner.setAttribute("translate", "no");
    banner.setAttribute("data-fidelidapp-cart-injected", "true");
    // Guest: los puntos se acreditan igual por email (pixel); el link lleva a la
    // vitrina para que vea qué se gana — sin meter login en el carrito.
    var subtext = cfg.email
      ? '<span class="fidelidapp-banner__subtext">¡Canjealos por recompensas en tu próxima compra!</span>'
      : '<a class="fidelidapp-banner__subtext fidelidapp-banner__link" href="/pages/' +
        escapeHtml(cfg.rewardsPageSlug) + '">¿Ya compraste antes? Mirá tus puntos →</a>';
    banner.innerHTML =
      '<span class="fidelidapp-banner__icon" aria-hidden="true">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>' +
      '</span>' +
      '<div class="fidelidapp-banner__text">' +
      '<span>' +
      escapeHtml(cfg.textCartBefore) + ' <strong class="fidelidapp-banner__highlight">' +
      points.toLocaleString() + '</strong> ' + escapeHtml(cfg.textCartAfter) +
      '</span>' +
      subtext +
      '</div>';
    return banner;
  }

  // ════════════════════════════════════════════════════
  // 4. CUENTA: recompensas en /account
  // ════════════════════════════════════════════════════

  async function initAccount() {
    var cfg = getConfig();
    if (!cfg || !cfg.enableAccount) return;
    var acctPath = window.location.pathname;
    if (acctPath.indexOf("/account") === -1) return;
    // No inyectar sobre las páginas de login/registro/challenge
    if (/\/account\/(login|register)|\/challenge/.test(acctPath)) return;
    // No interferir con la pagina de recompensas
    if (isRewardsPage()) return;

    if (!cfg.email) {
      injectAccountWidget(buildLoginHtml());
      return;
    }

    try {
      var data = await apiGet(
        "/api/shopify/public/rewards?shop=" + encodeURIComponent(cfg.shop) +
        "&email=" + encodeURIComponent(cfg.email)
      );

      var html = buildRewardsWidgetHtml(data, false);
      var container = injectAccountWidget(html);
      if (container) {
        bindRedeemButtons(container);
        bindBirthdayForm(cfg);
      }
    } catch (e) {
      console.warn("[FidelidApp] Error en cuenta:", e);
    }
  }

  function injectAccountWidget(html) {
    if (document.querySelector("[data-fidelidapp-account-widget]")) return null;
    var wrapper = document.createElement("div");
    wrapper.className = "notranslate";
    wrapper.setAttribute("translate", "no");
    wrapper.setAttribute("data-fidelidapp-injected", "true");
    wrapper.setAttribute("data-fidelidapp-account-widget", "true");
    wrapper.innerHTML = html;

    var targets = [
      "main .customer",
      "main .account",
      "#MainContent .customer",
      "#MainContent .account",
      ".template-customers-account main",
      "main #MainContent",
      "main",
      "#MainContent",
      ".main-content",
      "#main",
    ];

    for (var i = 0; i < targets.length; i++) {
      var el = document.querySelector(targets[i]);
      if (el) {
        el.insertBefore(wrapper, el.firstChild);
        return wrapper;
      }
    }

    document.body.appendChild(wrapper);
    return wrapper;
  }

  // ════════════════════════════════════════════════════
  // 5. PAGINA DE RECOMPENSAS: /pages/recompensas
  //    El merchant crea una pagina en blanco en Shopify
  //    y el widget la llena con el UI completo.
  // ════════════════════════════════════════════════════

  function isRewardsPage() {
    var cfg = getConfig();
    if (!cfg || !cfg.enableRewardsPage) return false;
    var path = window.location.pathname.toLowerCase();
    var slug = cfg.rewardsPageSlug.toLowerCase();
    return path === "/pages/" + slug || path === "/pages/" + slug + "/";
  }

  async function initRewardsPage() {
    var cfg = getConfig();
    if (!cfg || !cfg.enableRewardsPage) return;
    if (!isRewardsPage()) return;

    // Selectores para excluir drawers y elementos de carrito
    var drawerExclude = 'cart-drawer, .cart-drawer, #CartDrawer, .drawer, .mini-cart, .side-cart, [data-cart-drawer], cart-notification';

    // Encontrar el contenido principal de la pagina, excluyendo drawers
    var pageContent = null;
    var contentSelectors = [
      '#MainContent .page-width',
      '#MainContent .shopify-section .page-width',
      'main .page-width',
      '#MainContent .rte',
      '#MainContent .page__content',
      'main .rte',
      '#MainContent',
      'main',
    ];

    for (var i = 0; i < contentSelectors.length; i++) {
      var candidates = document.querySelectorAll(contentSelectors[i]);
      for (var j = 0; j < candidates.length; j++) {
        // Excluir elementos dentro de drawers
        if (!candidates[j].closest(drawerExclude)) {
          pageContent = candidates[j];
          break;
        }
      }
      if (pageContent) break;
    }

    // Fallback: buscar main o #MainContent pero NO dentro de un drawer
    if (!pageContent) {
      var fallbacks = document.querySelectorAll('#MainContent, main, .main-content');
      for (var f = 0; f < fallbacks.length; f++) {
        if (!fallbacks[f].closest(drawerExclude)) {
          pageContent = fallbacks[f];
          break;
        }
      }
    }

    if (!pageContent) return;

    // Si el target es un bloque angosto (.rte, .shopify-block, .page__content),
    // escapar al padre para heredar el ancho completo de la secci\u00F3n
    var narrowClasses = ['rte', 'shopify-block', 'page__content'];
    var isNarrow = narrowClasses.some(function(cls) { return pageContent.classList.contains(cls); });
    var insertionParent = pageContent;
    var insertionRef = null;

    if (isNarrow && pageContent.parentNode) {
      insertionRef = pageContent;        // insertBefore este nodo angosto
      insertionParent = pageContent.parentNode;
      pageContent.style.display = 'none'; // ocultar bloque angosto
    } else {
      // Limpiar contenido existente si est\u00E1 vac\u00EDo o tiene poco contenido
      var existingText = pageContent.textContent.trim();
      if (existingText.length < 100) {
        pageContent.innerHTML = '';
      }
    }

    // Ensanchar el contenedor angosto del theme para usar más ancho y que el TÍTULO
    // de la página quede alineado con las cards (se ensanchan juntos).
    // Horizon (y themes derivados) limitan el ancho con la variable CSS --page-width
    // y `width: var(--page-width) !important`, así que NO alcanza con max-width/width:
    // hay que sobreescribir la variable. Dejamos max-width como fallback para otros themes.
    try {
      var widthHost = insertionParent.closest &&
        insertionParent.closest('[class*="page-width"], .page__content, .container, .section, main');
      if (widthHost) {
        widthHost.style.setProperty("--page-width", "min(1140px, 100%)");
        // Horizon V4: el ancho de contenido sale de --page-content-width /
        // --normal-content-width (36-45rem), no de --page-width. Pisar las tres.
        widthHost.style.setProperty("--page-content-width", "min(1140px, 100%)");
        widthHost.style.setProperty("--normal-content-width", "min(1140px, 100%)");
        widthHost.style.maxWidth = "1140px";
        widthHost.style.marginLeft = "auto";
        widthHost.style.marginRight = "auto";
      }

      // nota: la hidratación/morph del theme pisa los estilos inline del nodo un
      // instante después (flash ancho→angosto). Regla en <head> con :has() +
      // !important: el morph no puede borrarla y se re-evalúa sola tras cada
      // re-render. El inline de arriba queda como refuerzo pre-:has.
      if (!document.getElementById("fidelidapp-rewards-width")) {
        var widthStyle = document.createElement("style");
        widthStyle.id = "fidelidapp-rewards-width";
        // OJO: nada de [class*="page-width"] a secas — el <body> de V4 tiene
        // clase page-width-narrow y un max-width en body rompe toda la página.
        widthStyle.textContent =
          '.section:has(.fidelidapp-rewards-page):not(body), ' +
          '.page-width-content:has(.fidelidapp-rewards-page):not(body) {' +
          '--page-width: min(1140px, 100%) !important;' +
          '--page-content-width: min(1140px, 100%) !important;' +
          '--normal-content-width: min(1140px, 100%) !important;' +
          'max-width: 1140px !important;' +
          'margin-left: auto !important;' +
          'margin-right: auto !important;' +
          '}';
        document.head.appendChild(widthStyle);
      }
    } catch (e) { /* si falla, el widget igual se muestra con el ancho del contenedor */ }

    // Mostrar loading
    var loadingDiv = document.createElement("div");
    loadingDiv.className = "fidelidapp-rewards-page notranslate";
    loadingDiv.setAttribute("translate", "no");
    loadingDiv.setAttribute("data-fidelidapp-injected", "true");
    loadingDiv.innerHTML = '<div class="fidelidapp-rewards-page__loading">Cargando recompensas\u2026</div>';
    insertionParent.insertBefore(loadingDiv, insertionRef); // insertionRef=null \u2192 equivale a appendChild

    if (!cfg.email) {
      loadingDiv.innerHTML = buildRewardsPageLoginHtml();
      loadGuestCatalog(loadingDiv, cfg);
      return;
    }

    _rewardsPageEl = loadingDiv;
    await loadRewardsPage(loadingDiv, cfg);
  }

  // Vitrina para visitantes sin sesión: muestra qué se puede canjear debajo del
  // login box. Es opcional — si el fetch falla, el login box queda solo.
  async function loadGuestCatalog(container, cfg) {
    try {
      var data = await apiGet(
        "/api/shopify/public/rewards?shop=" + encodeURIComponent(cfg.shop)
      );
      var items = (data && data.rewards) || [];
      if (!items.length) return;

      var html = '<div class="fidelidapp-catalog">';
      html += '<p class="fidelidapp-rewards-page__section-label">Lo que podés canjear</p>';
      html += '<div class="fidelidapp-rewards-page__list fidelidapp-rewards-page__list--catalog">';
      for (var i = 0; i < items.length; i++) {
        var r = items[i];
        try {
          html += '<div class="fidelidapp-reward fidelidapp-reward--catalog">';
          if (r.shopifyProductImage) {
            html += '<img src="' + escapeHtml(r.shopifyProductImage) + '" class="fidelidapp-reward__image" alt="" loading="lazy" />';
          } else {
            html += buildRewardArtHtml(r.rewardType);
          }
          html += '<div class="fidelidapp-reward__info">';
          html += '<span class="fidelidapp-reward__desc">' + escapeHtml(r.description) + '</span>';
          html += '<span class="fidelidapp-reward__points">' + (Number(r.points) || 0).toLocaleString() + ' pts</span>';
          html += '</div>';
          html += '</div>';
        } catch (cardErr) { /* una recompensa rota no voltea la vitrina */ }
      }
      html += '</div>';
      html += '</div>';

      // Al contenedor EXTERNO: el box de login tiene max-width 420px y ahogaba
      // la vitrina; afuera hereda el ancho completo de la página.
      container.insertAdjacentHTML('beforeend', html); // Safe: valores escapados con escapeHtml
    } catch (e) {
      console.warn('[FidelidApp] Vitrina guest no disponible:', e);
    }
  }

  // Fetch de recompensas + carrito, render y binds de la página de recompensas.
  // Se reutiliza para re-renderizar después de un canje exitoso (aparece el código
  // activo y se bloquean las demás cards sin recargar).
  async function loadRewardsPage(loadingDiv, cfg) {
    try {
      var data = await apiGet(
        "/api/shopify/public/rewards?shop=" + encodeURIComponent(cfg.shop) +
        "&email=" + encodeURIComponent(cfg.email)
      );

      // Fetch cart to check which products are already added
      var cartItems = [];
      try {
        var cartData = await fetch("/cart.js").then(function (r) { return r.json(); });
        cartItems = cartData.items || [];
      } catch (e) { /* ignore — si falla, no bloqueamos el botón */ }

      loadingDiv.innerHTML = buildRewardsPageHtml(data, cfg.customerName, cartItems); // All user content escaped via escapeHtml()
      bindRedeemButtons(loadingDiv);
      bindAddToCartButtons(loadingDiv, loadingDiv);
      bindActiveCodeCta(loadingDiv);
      bindBirthdayForm(cfg);

      // Fetch and append special promotions (all content escaped via escapeHtml)
      try {
        var promoData = await fetchPromotions(cfg);
        if (promoData.promotions && promoData.promotions.length > 0) {
          var promoHtml = buildPromotionsSectionHtml(promoData.promotions);
          var rewardsPageDiv = loadingDiv.querySelector(".fidelidapp-rewards-page__list");
          if (rewardsPageDiv && rewardsPageDiv.parentNode) {
            var promoContainer = document.createElement("div");
            promoContainer.innerHTML = promoHtml; // Safe: all dynamic values escaped via escapeHtml()
            rewardsPageDiv.parentNode.insertBefore(promoContainer.firstChild, rewardsPageDiv.nextSibling);
          } else {
            loadingDiv.insertAdjacentHTML("beforeend", promoHtml); // Safe: escapeHtml used on all user data
          }
          bindClaimButtons(loadingDiv);
        }
      } catch (promoErr) {
        console.warn("[FidelidApp] Error cargando promociones:", promoErr);
      }
    } catch (e) {
      loadingDiv.innerHTML =
        '<div class="fidelidapp-rewards-page">' +
        '<p style="text-align:center;color:#6b7280;">Error al cargar recompensas. Intenta de nuevo.</p>' +
        '</div>';
      console.warn("[FidelidApp] Error en pagina de recompensas:", e);
    }
  }

  function bindBirthdayForm(cfg) {
    var btn = document.getElementById("fidelidapp-birthday-btn");
    var input = document.getElementById("fidelidapp-birthday-input");
    if (!btn || !input) return;

    btn.addEventListener("click", function () {
      var value = input.value;
      if (!value) {
        showBirthdayMsg("Selecciona una fecha", true);
        return;
      }

      var date = new Date(value + "T00:00:00");
      if (isNaN(date.getTime()) || date > new Date()) {
        showBirthdayMsg("La fecha no es válida", true);
        return;
      }

      btn.disabled = true;
      btn.textContent = "Guardando...";

      apiPost("/api/shopify/public/update-profile", {
        shop: cfg.shop,
        email: cfg.email,
        birthDate: value,
      }).then(function (res) {
        if (res.ok && res.data.success) {
          var section = document.getElementById("fidelidapp-birthday-section");
          if (section) {
            section.innerHTML =
              '<div class="fidelidapp-birthday__icon">' +
              '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>' +
              '</div>' +
              '<div class="fidelidapp-birthday__content">' +
              '<p class="fidelidapp-birthday__text" style="color: #059669;">Fecha de nacimiento registrada correctamente</p>' +
              '</div>';
            section.classList.add("fidelidapp-birthday--success");
          }
        } else {
          showBirthdayMsg(res.data.error || "Error al guardar", true);
          btn.disabled = false;
          btn.textContent = "Guardar";
        }
      }).catch(function () {
        showBirthdayMsg("Error al guardar. Intenta de nuevo.", true);
        btn.disabled = false;
        btn.textContent = "Guardar";
      });
    });
  }

  function showBirthdayMsg(text, isError) {
    var msg = document.getElementById("fidelidapp-birthday-msg");
    if (!msg) return;
    msg.textContent = text;
    msg.style.color = isError ? "#dc2626" : "#059669";
  }

  function buildRewardsPageLoginHtml() {
    return '<div class="fidelidapp-rewards-page fidelidapp-rewards-page--login">' +
      '<div class="fidelidapp-rewards-page__login-box">' +
      '<div class="fidelidapp-rewards-page__login-copy">' +
      '<h2 class="fidelidapp-rewards-page__login-title">Programa de Recompensas</h2>' +
      '<p class="fidelidapp-rewards-page__login-desc">Mirá tus puntos y canjeá recompensas. Solo necesitás tu email: te mandamos un código de 6 dígitos, <strong>sin contraseña</strong>.</p>' +
      '<p class="fidelidapp-rewards-page__login-hint">¿Compraste como invitado? Usá el mismo email de tu compra y vas a ver tus puntos acumulados.</p>' +
      '</div>' +
      '<a href="' + escapeHtml(getLoginUrl()) + '" class="fidelidapp-reward__btn fidelidapp-rewards-page__login-cta">Ver mis puntos</a>' +
      '</div></div>';
  }

  function buildRewardCardHtml(r, myPoints, isBlocked, cartItems) {
    isBlocked = isBlocked || false;
    cartItems = cartItems || [];

    var cls = r.canRedeem ? "fidelidapp-reward--available" : "fidelidapp-reward--locked";
    var html = '<div class="fidelidapp-reward ' + cls + '" data-reward-id-card="' + escapeHtml(r._id || r.id) + '" data-reward-type="' + escapeHtml(r.rewardType || '') + '">';

    // Imagen de producto, o ilustración del tipo de recompensa (simetría del grid)
    if (r.shopifyProductImage && (r.rewardType === "product_discount" || r.rewardType === "free_product")) {
      html += '<img src="' + escapeHtml(r.shopifyProductImage) + '" class="fidelidapp-reward__image" alt="" />';
    } else {
      html += buildRewardArtHtml(r.rewardType);
    }

    html += '<div class="fidelidapp-reward__info">';
    html += '<span class="fidelidapp-reward__desc">' + escapeHtml(r.description) + '</span>';

    var reqPts = Number(r.points) || 0;
    html += '<span class="fidelidapp-reward__points">' + reqPts.toLocaleString() + ' pts</span>';

    if (!r.canRedeem && reqPts > 0) {
      var missing = Math.max(0, reqPts - myPoints);
      html += '<div class="fidelidapp-reward__progress">';
      var pct = Math.min(100, Math.round((myPoints / reqPts) * 100));
      html += '<div class="fidelidapp-reward__progress-bar"><div class="fidelidapp-reward__progress-fill" style="width: ' + pct + '%"></div></div>';
      html += '<span class="fidelidapp-reward__progress-text">Te faltan ' + missing.toLocaleString() + ' pts</span>';
      html += '</div>';
    }

    html += '</div>';

    if (r.available === false) {
      html += '<div class="fidelidapp-reward__btn-row">';
      html += '<button class="fidelidapp-reward__btn" disabled style="opacity:0.5;cursor:not-allowed;">No disponible</button>';
      html += '</div>';
    } else if (r.canRedeem && !isBlocked) {
      // Para product_discount: verificar que el producto esté en el carrito
      if (r.rewardType === "product_discount" || r.rewardType === "free_product") {
        var productInCart = r.shopifyProductId && cartItems.some(function (item) {
          return String(item.product_id) === String(r.shopifyProductId);
        });
        if (!productInCart && r.shopifyProductId) {
          var variantAttr = r.shopifyProductVariantId ? ' data-variant-id="' + escapeHtml(String(r.shopifyProductVariantId)) + '"' : '';
          html += '<div class="fidelidapp-reward__btn-row">';
          html += '<button class="fidelidapp-reward__btn--add-cart" data-product-gid="' + escapeHtml(r.shopifyProductGid || '') + '"' + variantAttr + '>Agregar al carrito</button>';
          html += '<button class="fidelidapp-reward__btn fidelidapp-reward__btn--blocked" disabled>Canjear</button>';
          html += '</div>';
        } else {
          html += '<div class="fidelidapp-reward__btn-row">';
          html += '<button class="fidelidapp-reward__btn" data-reward-id="' + escapeHtml(r._id || r.id) + '">Canjear</button>';
          html += '</div>';
        }
      } else if (r.rewardType === "collection_discount") {
        var collectionLabel = r.shopifyCollectionTitle ? escapeHtml(r.shopifyCollectionTitle) : "la colección";
        html += '<div class="fidelidapp-reward__cart-hint">El descuento aplica solo a productos de ' + collectionLabel + '</div>';
        html += '<div class="fidelidapp-reward__btn-row">';
        html += '<button class="fidelidapp-reward__btn" data-reward-id="' + escapeHtml(r._id || r.id) + '">Canjear</button>';
        html += '</div>';
      } else {
        html += '<div class="fidelidapp-reward__btn-row">';
        html += '<button class="fidelidapp-reward__btn" data-reward-id="' + escapeHtml(r._id || r.id) + '">Canjear</button>';
        html += '</div>';
      }
    } else if (r.canRedeem && isBlocked) {
      html += '<button class="fidelidapp-reward__btn fidelidapp-reward__btn--blocked" disabled>Código activo</button>';
    } else {
      html += '<span class="fidelidapp-reward__locked-label">Bloqueado</span>';
    }
    html += '</div>';
    return html;
  }

  function buildRewardsPageHtml(data, customerName, cartItems) {
    var points = data.points || 0;
    var rewards = data.rewards || [];
    cartItems = cartItems || [];
    var ppp = data.pointsPerPurchase || 1;
    var amountPerPoint = ppp > 0 ? Math.round(1 / ppp) : 1;
    var earnRateText = amountPerPoint <= 1
      ? '1 punto por cada $1 de compra'
      : '1 punto por cada $' + amountPerPoint.toLocaleString() + ' de compra';

    var activeCode = data.activeCode || null;
    // Un código vencido (o con fecha inválida) no debe mostrarse como activo
    // ni bloquear canjes nuevos
    if (activeCode && activeCode.expiresAt) {
      var activeExpTs = new Date(activeCode.expiresAt).getTime();
      if (isNaN(activeExpTs) || activeExpTs < Date.now()) activeCode = null;
    }
    var oneActiveRedemption = data.oneActiveRedemption || false;
    var isBlocked = oneActiveRedemption && !!activeCode;

    var html = '<div class="fidelidapp-rewards-page">';

    // Header con puntos (card horizontal arriba de las recompensas)
    html += '<div class="fidelidapp-rewards-page__header">';
    html += '<div class="fidelidapp-rewards-page__points-display">';
    html += '<span class="fidelidapp-rewards-page__points-number" data-my-points>' + points.toLocaleString() + '</span>';
    html += '<span class="fidelidapp-rewards-page__points-label">puntos</span>';
    html += '</div>';

    html += '<div class="fidelidapp-rewards-page__header-text">';
    if (customerName) {
      html += '<h2 class="fidelidapp-rewards-page__greeting">Hola, ' + escapeHtml(customerName) + '</h2>';
    }
    html += '<p class="fidelidapp-rewards-page__subtitle">Canjea tus puntos por recompensas exclusivas</p>';
    html += '<p class="fidelidapp-rewards-page__earn-rate">' + earnRateText + '</p>';
    html += '</div>';

    // Barra de progreso global hacia el canje más cercano (o aviso si ya puede canjear)
    var redeemableCount = 0;
    var nextReward = null;
    for (var ri = 0; ri < rewards.length; ri++) {
      var rr = rewards[ri];
      var rrPoints = Number(rr.points) || 0;
      if (rr.available === false || rrPoints <= 0) continue;
      if (rr.canRedeem) {
        redeemableCount++;
      } else if (!nextReward || rrPoints < (Number(nextReward.points) || 0)) {
        nextReward = rr;
      }
    }
    if (redeemableCount > 0) {
      html += '<div class="fidelidapp-global-progress fidelidapp-global-progress--ready">' +
        '<span class="fidelidapp-global-progress__label">¡Tenés ' +
        (redeemableCount === 1 ? 'una recompensa lista para canjear' : redeemableCount + ' recompensas listas para canjear') +
        '! Elegí la tuya abajo.</span>' +
        '</div>';
    } else if (nextReward) {
      var nextPts = Number(nextReward.points) || 0;
      var globalPct = nextPts > 0 ? Math.min(100, Math.round((points / nextPts) * 100)) : 0;
      var globalMissing = Math.max(0, nextPts - points);
      html += '<div class="fidelidapp-global-progress">' +
        '<span class="fidelidapp-global-progress__label">Te faltan <strong>' + globalMissing.toLocaleString() +
        ' pts</strong> para: ' + escapeHtml(nextReward.description || 'tu próxima recompensa') + '</span>' +
        '<div class="fidelidapp-global-progress__track">' +
        '<div class="fidelidapp-global-progress__bar"><div class="fidelidapp-global-progress__fill" style="width:' + globalPct + '%"></div></div>' +
        '<span class="fidelidapp-global-progress__pct">' + globalPct + '%</span>' +
        '</div>' +
        '</div>';
    }
    html += '</div>';

    if (activeCode) {
      var expiryDate = activeCode.expiresAt
        ? new Date(activeCode.expiresAt).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;
      html += '<div class="fidelidapp-active-code" id="fidelidapp-active-code-banner">';
      html += '<div class="fidelidapp-active-code__label">Código activo</div>';
      // De qué recompensa es el código
      if (activeCode.rewardDescription) {
        html += '<div class="fidelidapp-active-code__reward">' + escapeHtml(activeCode.rewardDescription) + '</div>';
      }
      html += '<div class="fidelidapp-active-code__code" data-code="' + escapeHtml(activeCode.code) + '" onclick="fidelidappCopyCode(this)">';
      html += escapeHtml(activeCode.code);
      html += '<span class="fidelidapp-active-code__copy">Copiar</span>';
      html += '</div>';
      if (expiryDate) {
        html += '<div class="fidelidapp-active-code__expiry">Vence ' + expiryDate + '</div>';
      }
      // Si el código es de un producto, permitir agregarlo al carrito y aplicar el descuento
      var isProductCode = (activeCode.rewardType === "product_discount" || activeCode.rewardType === "free_product")
        && (activeCode.shopifyProductGid || activeCode.shopifyProductVariantId);
      if (isProductCode) {
        var acVariantAttr = activeCode.shopifyProductVariantId
          ? ' data-variant-id="' + escapeHtml(String(activeCode.shopifyProductVariantId)) + '"'
          : '';
        html += '<button class="fidelidapp-active-code__cta" data-active-code-cta'
          + ' data-product-gid="' + escapeHtml(activeCode.shopifyProductGid || '') + '"'
          + acVariantAttr
          + ' data-code="' + escapeHtml(activeCode.code) + '">Agregar al carrito y aplicar</button>';
      }
      if (oneActiveRedemption) {
        html += '<div class="fidelidapp-active-code__hint">Usá este código antes de canjear una nueva recompensa</div>';
      }
      html += '</div>';
    }

    // Birthday input si no tiene fecha de nacimiento
    if (!data.hasBirthDate) {
      html += '<div class="fidelidapp-birthday" id="fidelidapp-birthday-section">';
      html += '<div class="fidelidapp-birthday__icon">';
      html += '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';
      html += '</div>';
      html += '<div class="fidelidapp-birthday__content">';
      html += '<p class="fidelidapp-birthday__text">Registra tu fecha de nacimiento para recibir promociones exclusivas</p>';
      html += '<div class="fidelidapp-birthday__form">';
      html += '<input type="date" id="fidelidapp-birthday-input" class="fidelidapp-birthday__input" />';
      html += '<button id="fidelidapp-birthday-btn" class="fidelidapp-birthday__btn">Guardar</button>';
      html += '</div>';
      html += '<p class="fidelidapp-birthday__note" id="fidelidapp-birthday-msg"></p>';
      html += '</div>';
      html += '</div>';
    }


    html += '<p class="fidelidapp-rewards-page__section-label">Recompensas disponibles</p>';

    // Lista de recompensas
    html += '<div class="fidelidapp-rewards-page__list">';

    if (rewards.length > 0) {
      for (var j = 0; j < rewards.length; j++) {
        // Una recompensa mal configurada no debe tirar abajo la p\u00E1gina entera
        try {
          html += buildRewardCardHtml(rewards[j], points, isBlocked, cartItems);
        } catch (cardErr) {
          console.warn("[FidelidApp] Recompensa mal configurada, se omite:", cardErr);
        }
      }
    } else {
      html += '<p class="fidelidapp-no-rewards">No hay recompensas disponibles todav\u00EDa. Sigue comprando para acumular puntos.</p>';
    }

    html += '</div>';

    html += '</div>';
    return html;
  }

  function fidelidappCopyCode(el) {
    var code = el.getAttribute('data-code');
    if (!code) return;
    navigator.clipboard.writeText(code).then(function() {
      var copySpan = el.querySelector('.fidelidapp-active-code__copy');
      if (copySpan) {
        var original = copySpan.textContent;
        copySpan.textContent = '¡Copiado!';
        setTimeout(function() { copySpan.textContent = original; }, 2000);
      }
    }).catch(function() {
      // Fallback para navegadores sin clipboard API
      var ta = document.createElement('textarea');
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
  }
  // Exponer como global para el onclick inline del banner
  window.fidelidappCopyCode = fidelidappCopyCode;

  // ─── Promotions section (special promotions with discount codes) ───

  function fetchPromotions(cfg) {
    return apiGet(
      "/api/shopify/public/promotions?shop=" + encodeURIComponent(cfg.shop) +
      "&email=" + encodeURIComponent(cfg.email)
    );
  }

  function buildScheduleInfo(promo) {
    var parts = [];
    if (promo.daysOfWeek && promo.daysOfWeek.length > 0 && promo.daysOfWeek.length < 7) {
      var dayNames = { 0: "Dom", 1: "Lun", 2: "Mar", 3: "Mié", 4: "Jue", 5: "Vie", 6: "Sáb" };
      var names = promo.daysOfWeek.map(function (d) { return dayNames[d] || d; });
      parts.push(names.join(", "));
    }
    if (promo.startTime && promo.endTime) {
      parts.push(promo.startTime + " - " + promo.endTime);
    }
    return parts.length > 0 ? parts.join(" · ") : "";
  }

  function buildDiscountBadge(promo) {
    var sd = promo.shopifyDiscount;
    if (!sd || !sd.discountType) return "";
    var text = "";
    if (sd.discountType === "percentage") {
      text = sd.discountValue + "% OFF";
    } else if (sd.discountType === "fixed_amount") {
      text = "$" + sd.discountValue + " OFF";
    } else if (sd.discountType === "free_shipping") {
      text = "Envío gratis";
    } else if (sd.discountType === "product_discount") {
      text = sd.discountValue + "% en " + escapeHtml(sd.shopifyProductTitle || "producto");
    } else if (sd.discountType === "collection_discount") {
      text = sd.discountValue + "% en " + escapeHtml(sd.shopifyCollectionTitle || "colección");
    }
    if (!text) return "";
    return '<span class="fidelidapp-promo__discount-badge">' + text + '</span>';
  }

  function buildPromotionsSectionHtml(promotions) {
    if (!promotions || promotions.length === 0) return "";

    var html = '<div class="fidelidapp-promotions-section">';
    html += '<h3 class="fidelidapp-rewards-page__section-title">Promociones Especiales</h3>';

    for (var i = 0; i < promotions.length; i++) {
      var p = promotions[i];
      var schedule = buildScheduleInfo(p);
      var badge = buildDiscountBadge(p);
      var isAvailable = p.availableNow && !p.alreadyClaimed;
      var cardClass = "fidelidapp-promo";
      if (p.alreadyClaimed) {
        cardClass += " fidelidapp-promo--claimed";
      } else if (!isAvailable) {
        cardClass += " fidelidapp-promo--unavailable";
      }

      html += '<div class="' + cardClass + '">';

      if (p.imageUrl) {
        html += '<div class="fidelidapp-promo__image-wrap">';
        html += '<img src="' + escapeHtml(p.imageUrl) + '" alt="' + escapeHtml(p.title) + '" class="fidelidapp-promo__image" loading="lazy">';
        html += '</div>';
      }

      html += '<div class="fidelidapp-promo__body">';
      html += '<div class="fidelidapp-promo__header">';
      html += '<h4 class="fidelidapp-promo__title">' + escapeHtml(p.title) + '</h4>';
      if (badge) html += badge;
      html += '</div>';

      if (p.description) {
        html += '<p class="fidelidapp-promo__desc">' + escapeHtml(p.description) + '</p>';
      }

      if (schedule) {
        html += '<div class="fidelidapp-promo__schedule">🕐 ' + escapeHtml(schedule) + '</div>';
      }

      if (p.conditions) {
        html += '<p class="fidelidapp-promo__conditions">' + escapeHtml(p.conditions) + '</p>';
      }

      // Already claimed - show discount code
      if (p.alreadyClaimed && p.discountCode) {
        html += '<div class="fidelidapp-promo__code-box">';
        html += '<span class="fidelidapp-promo__code-label">Tu código:</span>';
        html += '<span class="fidelidapp-promo__code">' + escapeHtml(p.discountCode) + '</span>';
        html += '</div>';
      } else if (p.alreadyClaimed) {
        html += '<span class="fidelidapp-promo__claimed-label">Ya canjeada</span>';
      } else if (isAvailable) {
        html += '<button class="fidelidapp-promo__btn" data-promo-id="' + p.id + '">Reclamar</button>';
      } else {
        var reason = "";
        if (!p.availableNow && schedule) {
          reason = "Disponible: " + schedule;
        } else {
          reason = "No disponible ahora";
        }
        html += '<span class="fidelidapp-promo__unavailable-label">' + escapeHtml(reason) + '</span>';
      }

      html += '</div></div>';
    }

    html += '</div>';
    return html;
  }

  function bindClaimButtons(container) {
    var buttons = container.querySelectorAll(".fidelidapp-promo__btn");
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener("click", handleClaimClick);
    }
  }

  async function handleClaimClick(e) {
    var btn = e.currentTarget;
    var promoId = btn.getAttribute("data-promo-id");
    if (!promoId || btn.disabled) return;

    var cfg = getConfig();
    btn.disabled = true;
    btn.textContent = "Procesando...";

    try {
      var result = await apiPost("/api/shopify/public/claim-promotion", {
        shop: cfg.shop,
        email: cfg.email,
        promotionId: promoId,
      });

      if (result.ok) {
        var card = btn.closest(".fidelidapp-promo");
        card.className = "fidelidapp-promo fidelidapp-promo--claimed";
        var code = result.data.discountCode;
        if (code) {
          btn.outerHTML =
            '<div class="fidelidapp-promo__code-box">' +
            '<span class="fidelidapp-promo__code-label">Tu código:</span>' +
            '<span class="fidelidapp-promo__code">' + escapeHtml(code) + '</span>' +
            '</div>';
        } else {
          btn.outerHTML = '<span class="fidelidapp-promo__claimed-label">Canjeada</span>';
        }
      } else {
        btn.disabled = false;
        btn.textContent = "Reclamar";
        var msg = (result.data && result.data.error) || "Error al reclamar";
        showActionToast(msg, "error");
      }
    } catch (err) {
      btn.disabled = false;
      btn.textContent = "Reclamar";
      console.warn("[FidelidApp] Error claiming promotion:", err);
      showActionToast("Error al procesar. Intenta de nuevo.", "error");
    }
  }

  // ─── Rewards widget compacto (para /account) ───

  function buildRewardsWidgetHtml(data, fullPage) {
    var points = data.points || 0;
    var rewards = data.rewards || [];
    var cfg = getConfig();
    var activeCode = data.activeCode || null;
    // Un código vencido (o con fecha inválida) no debe mostrarse como activo
    // ni bloquear canjes nuevos
    if (activeCode && activeCode.expiresAt) {
      var activeExpTs = new Date(activeCode.expiresAt).getTime();
      if (isNaN(activeExpTs) || activeExpTs < Date.now()) activeCode = null;
    }
    var oneActiveRedemption = data.oneActiveRedemption || false;
    var isBlocked = oneActiveRedemption && !!activeCode;

    var html = '<div class="fidelidapp-rewards">';
    html += '<div class="fidelidapp-rewards__header">';
    html += '<h2 class="fidelidapp-rewards__title">Mis Recompensas</h2>';
    html += '<div class="fidelidapp-rewards__points-badge">\u2B50 <span data-my-points>' + points.toLocaleString() + '</span> puntos</div>';
    html += '</div>';

    if (cfg.customerName) {
      html += '<p style="color: #6b7280; margin: 0 0 16px 0;">Hola, <strong>' + escapeHtml(cfg.customerName) + '</strong></p>';
    }

    if (activeCode) {
      html += '<div class="fidelidapp-active-code" style="margin-bottom: 12px;">';
      html += '<div class="fidelidapp-active-code__label">C\u00F3digo activo</div>';
      html += '<div class="fidelidapp-active-code__code" data-code="' + escapeHtml(activeCode.code) + '" onclick="fidelidappCopyCode(this)">';
      html += escapeHtml(activeCode.code);
      html += '<span class="fidelidapp-active-code__copy">Copiar</span>';
      html += '</div>';
      if (oneActiveRedemption) {
        html += '<div class="fidelidapp-active-code__hint">Us\u00E1 este c\u00F3digo antes de canjear una nueva recompensa</div>';
      }
      html += '</div>';
    }

    if (rewards.length > 0) {
      for (var j = 0; j < rewards.length; j++) {
        var r = rewards[j];
        var cls = r.canRedeem ? "fidelidapp-reward--available" : "fidelidapp-reward--locked";
        var rPts = Number(r.points) || 0;
        var isProductReward = (r.rewardType === "product_discount" || r.rewardType === "free_product");
        html += '<div class="fidelidapp-reward ' + cls + '" data-reward-type="' + escapeHtml(r.rewardType || '') + '">';
        html += '<div class="fidelidapp-reward__info">';
        html += '<span class="fidelidapp-reward__desc">' + escapeHtml(r.description) + '</span>';
        html += '<span class="fidelidapp-reward__points">' + rPts.toLocaleString() + ' pts</span>';
        html += '</div>';
        if (r.canRedeem && !isBlocked && isProductReward && cfg.enableRewardsPage) {
          // El canje de producto necesita el flujo completo (agregar al carrito):
          // se resuelve en la p\u00E1gina de recompensas, no ac\u00E1
          html += '<a class="fidelidapp-reward__btn" style="text-decoration:none;" href="/pages/' + escapeHtml(cfg.rewardsPageSlug) + '">Canjear \u2192</a>';
        } else if (r.canRedeem && !isBlocked) {
          html += '<button class="fidelidapp-reward__btn" data-reward-id="' + escapeHtml(r._id || r.id) + '">Canjear</button>';
        } else if (r.canRedeem && isBlocked) {
          html += '<button class="fidelidapp-reward__btn fidelidapp-reward__btn--blocked" disabled>C\u00F3digo activo</button>';
        } else {
          html += '<span class="fidelidapp-reward__locked-label">Faltan ' + Math.max(0, rPts - points).toLocaleString() + ' pts</span>';
        }
        html += '</div>';
      }
    } else {
      html += '<p class="fidelidapp-no-rewards">No hay recompensas disponibles todav\u00EDa</p>';
    }

    // Birthday input si no tiene fecha de nacimiento
    if (!data.hasBirthDate) {
      html += '<div class="fidelidapp-birthday" id="fidelidapp-birthday-section" style="margin-top: 16px;">';
      html += '<div class="fidelidapp-birthday__icon">';
      html += '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';
      html += '</div>';
      html += '<div class="fidelidapp-birthday__content">';
      html += '<p class="fidelidapp-birthday__text">Registra tu fecha de nacimiento para recibir promociones exclusivas</p>';
      html += '<div class="fidelidapp-birthday__form">';
      html += '<input type="date" id="fidelidapp-birthday-input" class="fidelidapp-birthday__input" />';
      html += '<button id="fidelidapp-birthday-btn" class="fidelidapp-birthday__btn">Guardar</button>';
      html += '</div>';
      html += '<p class="fidelidapp-birthday__note" id="fidelidapp-birthday-msg"></p>';
      html += '</div>';
      html += '</div>';
    }

    // Link a pagina completa de recompensas
    if (cfg.enableRewardsPage) {
      html += '<div style="text-align: center; margin-top: 16px;">' +
        '<a href="/pages/' + escapeHtml(cfg.rewardsPageSlug) + '" ' +
        'style="color: #7c3aed; font-size: 14px; font-weight: 600; text-decoration: none;">' +
        'Ver todas las recompensas \u2192</a></div>';
    }

    html += '</div>';
    return html;
  }

  function buildLoginHtml() {
    return '<div class="fidelidapp-rewards" style="text-align: center; padding: 32px;">' +
      '<span class="fidelidapp-rewards__login-icon">' + FW_ICONS.gift + '</span>' +
      '<p style="color: #6b7280; margin: 0 0 16px 0;">Ingres\u00E1 con tu email para ver tus puntos \u2014 sin contrase\u00F1a, te mandamos un c\u00F3digo</p>' +
      '<a href="' + escapeHtml(getLoginUrl()) + '" class="fidelidapp-reward__btn" style="display: inline-block; text-decoration: none;">Ver mis puntos</a>' +
      '</div>';
  }

  // ─── Canje de recompensas (compartido entre account y rewards page) ───

  function bindRedeemButtons(container) {
    var btns = container.querySelectorAll("[data-reward-id]");
    for (var k = 0; k < btns.length; k++) {
      (function (btn) {
        btn.addEventListener("click", function () {
          handleRedeem(btn, container);
        });
      })(btns[k]);
    }
  }

  async function handleRedeem(btn, container) {
    var cfg = getConfig();
    var rewardId = btn.getAttribute("data-reward-id");
    var originalText = btn.textContent;
    btn.textContent = "Canjeando...";
    btn.disabled = true;

    try {
      var result = await apiPost("/api/shopify/public/redeem", {
        shop: cfg.shop,
        email: cfg.email,
        rewardId: rewardId,
      });

      if (result.ok && result.data.success) {
        var data = result.data;
        var rewardDiv = btn.closest(".fidelidapp-reward");
        var rewardType = rewardDiv ? rewardDiv.getAttribute("data-reward-type") : "";

        // Para recompensas de producto: redirigir directamente con el descuento aplicado
        if (rewardType === "product_discount" || rewardType === "free_product") {
          window.location.href = '/discount/' + encodeURIComponent(data.discountCode) + '?redirect=/cart';
          return;
        }

        // El canje YA se concret\u00f3 (puntos descontados, c\u00f3digo creado): a partir de
        // ac\u00e1 ning\u00fan problema de UI debe reportarse como error \u2014 el usuario
        // reintentar\u00eda un canje ya hecho.
        try {
          if (data.discountCode) {
            showActionToast('\u00a1Canje exitoso! Tu c\u00f3digo: ' + data.discountCode, 'success');
          }

          if (_rewardsPageEl) {
            // P\u00e1gina de recompensas: re-render completo \u2014 aparece el banner de
            // c\u00f3digo activo y se bloquean las dem\u00e1s cards si corresponde
            await loadRewardsPage(_rewardsPageEl, cfg);
            var activeBanner = document.getElementById('fidelidapp-active-code-banner');
            if (activeBanner && activeBanner.scrollIntoView) {
              activeBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } else if (rewardDiv) {
            // Widget compacto de /account: reemplazar la card mostrando el c\u00f3digo
            var remaining = (typeof data.remainingPoints === 'number') ? data.remainingPoints : null;
            rewardDiv.className = "fidelidapp-reward fidelidapp-reward--redeemed";
            rewardDiv.innerHTML =
              '<div class="fidelidapp-reward__redeemed">' +
              '<span class="fidelidapp-reward__desc">' + FW_ICONS.check + ' ' + escapeHtml(data.rewardDescription || "Recompensa canjeada") + '</span>' +
              (data.discountCode
                ? '<div class="fidelidapp-active-code__code" data-code="' + escapeHtml(data.discountCode) + '" onclick="fidelidappCopyCode(this)">' +
                  escapeHtml(data.discountCode) +
                  '<span class="fidelidapp-active-code__copy">Copiar</span></div>'
                : '') +
              '<div class="fidelidapp-discount-actions">' +
              '<a href="/discount/' + encodeURIComponent(data.discountCode) + '?redirect=/cart" class="fidelidapp-discount-actions__apply">' +
              'Aplicar descuento al carrito \u2192</a>' +
              '</div>' +
              (remaining !== null
                ? '<span class="fidelidapp-reward__remaining">Puntos restantes: ' + remaining.toLocaleString() + '</span>'
                : '') +
              '</div>';

            if (remaining !== null) {
              var pointsEls = container.querySelectorAll("[data-my-points]");
              for (var p = 0; p < pointsEls.length; p++) {
                pointsEls[p].textContent = remaining.toLocaleString();
              }
            }
          }
        } catch (uiErr) {
          console.warn("[FidelidApp] Canje OK, error de UI post-canje:", uiErr);
        }
      } else {
        var errText = (result.data && result.data.error) || "Error";
        btn.textContent = errText;
        showActionToast(errText, 'error');
        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 3000);
      }
    } catch (e) {
      btn.textContent = "Error de conexi\u00f3n";
      setTimeout(function () {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 3000);
    }
  }

  // Tras agregar al carrito por nuestra cuenta (/cart/add.js), el theme no se entera
  // y no refresca el contador/drawer del carrito. Le avisamos disparando los eventos
  // que usan los themes más comunes. Verificado en Horizon: cart-icon escucha
  // "cart:update" y lee event.detail.data.itemCount.
  function notifyThemeCartUpdated() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart && typeof cart.item_count === 'number' ? cart.item_count : undefined;
        // Horizon (Shopify theme nuevo, web components)
        try {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { itemCount: count, source: 'fidelidapp-widget' }, source: 'fidelidapp-widget' },
          }));
        } catch (e) {}
        // Otros themes que escuchan eventos DOM de carrito
        ['cart:refresh', 'cart:updated', 'cart-updated'].forEach(function (name) {
          try { document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { cart: cart, source: 'fidelidapp-widget' } })); } catch (e) {}
        });
        // Dawn y derivados (pub/sub global)
        try { if (typeof window.publish === 'function') window.publish('cart-update', { source: 'fidelidapp-widget', cartData: cart }); } catch (e) {}
        // Fallback universal: actualizar contadores de carrito comunes
        if (count !== undefined) {
          try {
            document.querySelectorAll('.cart-count, [data-cart-count], .cart-count-bubble, .cart-link__bubble-num, .header__cart-count, .site-header__cart-count').forEach(function (el) {
              el.textContent = count;
            });
          } catch (e) {}
        }
      })
      .catch(function () {});
  }

  function handleAddToCart(btn, outerContainer) {
    var productGid = btn.getAttribute('data-product-gid');
    // Step 1: resolve variantId — use configured variant if available, else fetch from backend.
    // A configured variant is enough on its own (the GID is only needed for the backend fallback).
    var configuredVariant = btn.getAttribute('data-variant-id');
    if (!productGid && !configuredVariant) return;

    var config = getConfig();
    if (!config || !config.shop) return;

    btn.disabled = true;
    btn.textContent = 'Agregando...';


    var variantPromise = configuredVariant
      ? Promise.resolve({ variantId: configuredVariant })
      : fetch(config.apiBase + '/api/shopify/public/product-variant?shop='
          + encodeURIComponent(config.shop) + '&productGid=' + encodeURIComponent(productGid))
          .then(function (res) { return res.json(); });

    variantPromise
      .then(function (data) {
        if (!data.variantId) throw new Error('Sin variante');

        // Step 2: add to Shopify cart
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: data.variantId, quantity: 1 }] }),
        });
      })
      .then(function(res) {
        if (!res.ok) throw new Error('Error al agregar');
        return res.json();
      })
      .then(function() {
        // Avisar al theme para que refresque el contador/drawer del carrito
        notifyThemeCartUpdated();

        // Step 3: canjear automáticamente y redirigir con el descuento aplicado
        var card = btn.closest('[data-reward-id-card]') || btn.parentNode.parentNode;
        var rewardId = card ? card.getAttribute('data-reward-id-card') : null;
        if (!rewardId) {
          showActionToast('¡Producto agregado! Buscá tu recompensa para canjear.', 'success');
          return;
        }

        btn.textContent = 'Canjeando...';

        return apiPost('/api/shopify/public/redeem', {
          shop: config.shop,
          email: config.email,
          rewardId: rewardId,
        });
      })
      .then(function(result) {
        if (!result) return; // ya se manejó arriba (sin rewardId)
        if (result.ok && result.data && result.data.success && result.data.discountCode) {
          window.location.href = '/discount/' + encodeURIComponent(result.data.discountCode) + '?redirect=/cart';
        } else {
          var errMsg = (result.data && result.data.error) || 'No se pudo canjear. Intentá de nuevo.';
          showActionToast(errMsg, 'error');
          btn.disabled = false;
          btn.textContent = 'Agregar al carrito';
        }
      })
      .catch(function() {
        btn.disabled = false;
        btn.textContent = 'Agregar al carrito';
        showActionToast('No se pudo agregar el producto. Intentá de nuevo.', 'error');
      });
  }

  function bindAddToCartButtons(container, outerContainer) {
    var btns = container.querySelectorAll('.fidelidapp-reward__btn--add-cart');
    for (var i = 0; i < btns.length; i++) {
      (function(btn) {
        btn.addEventListener('click', function() {
          handleAddToCart(btn, outerContainer);
        });
      })(btns[i]);
    }
  }

  // Botón del aviso de código activo: agrega el producto al carrito y aplica el
  // descuento (redirige a /discount/<code> que aplica el código y muestra el carrito).
  function handleActiveCodeCta(btn) {
    var productGid = btn.getAttribute('data-product-gid');
    var configuredVariant = btn.getAttribute('data-variant-id');
    var code = btn.getAttribute('data-code');
    if ((!productGid && !configuredVariant) || !code) return;

    var config = getConfig();
    if (!config || !config.shop) return;

    btn.disabled = true;
    btn.textContent = 'Agregando...';

    var variantPromise = configuredVariant
      ? Promise.resolve({ variantId: configuredVariant })
      : fetch(config.apiBase + '/api/shopify/public/product-variant?shop='
          + encodeURIComponent(config.shop) + '&productGid=' + encodeURIComponent(productGid))
          .then(function (res) { return res.json(); });

    variantPromise
      .then(function (data) {
        if (!data.variantId) throw new Error('Sin variante');
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: data.variantId, quantity: 1 }] }),
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Error al agregar');
        return res.json();
      })
      .then(function () {
        // Aplicar el código de descuento y llevar al carrito
        window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = 'Agregar al carrito y aplicar';
        showActionToast('No se pudo agregar el producto. Intentá de nuevo.', 'error');
      });
  }

  function bindActiveCodeCta(container) {
    var btns = container.querySelectorAll('[data-active-code-cta]');
    for (var i = 0; i < btns.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () { handleActiveCodeCta(btn); });
      })(btns[i]);
    }
  }

  // ════════════════════════════════════════════════════
  // 6. REWARDS NAVIGATION: floating button + header link
  // ════════════════════════════════════════════════════

  function initRewardsNavigation() {
    var cfg = getConfig();
    if (!cfg || !cfg.enableRewardsPage) return;

    var rewardsUrl = "/pages/" + cfg.rewardsPageSlug;

    // No mostrar en la propia pagina de recompensas
    if (isRewardsPage()) return;

    // A) Top bar promocional
    initRewardsTopBar(rewardsUrl, cfg);

    // B) Intentar inyectar link en el header nav
    initRewardsHeaderLink(rewardsUrl);
  }

  // Barra hermana de la announcement bar del theme: misma altura y tono tipogr\u00E1fico;
  // el estado "canjeable" usa el color de acento del theme como \u00FAnico destaque.
  function initRewardsTopBar(url, cfg) {
    // No duplicar
    if (document.querySelector("[data-fidelidapp-topbar]")) return;

    // Cerrada hace menos de 7 d\u00EDas \u2192 no volver a molestar
    try {
      var closedUntil = parseInt(localStorage.getItem("fidelidapp-topbar-closed-until") || "0", 10);
      if (closedUntil && Date.now() < closedUntil) return;
    } catch (err) { }

    // Texto por defecto (visitante no logueado)
    var defaultText =
      'Sum\u00E1 puntos con cada compra \u2014 <strong>Conoc\u00E9 el programa</strong>';

    var topbar = document.createElement("div");
    topbar.className = "fidelidapp-topbar notranslate";
    topbar.setAttribute("translate", "no");
    topbar.setAttribute("data-fidelidapp-topbar", "true");
    topbar.innerHTML =
      '<a href="' + url + '" class="fidelidapp-topbar__link">' +
      '<span class="fidelidapp-topbar__text" data-fidelidapp-topbar-text>' +
      defaultText +
      '</span>' +
      '<span class="fidelidapp-topbar__arrow" aria-hidden="true">\u2192</span>' +
      '</a>' +
      '<button class="fidelidapp-topbar__close" aria-label="Cerrar">\u2715</button>';

    // Insertar al inicio del body, antes de todo
    document.body.insertBefore(topbar, document.body.firstChild);

    // Boton de cerrar: recuerda 7 d\u00EDas (localStorage), fallback por sesi\u00F3n
    var closeBtn = topbar.querySelector(".fidelidapp-topbar__close");
    closeBtn.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      topbar.style.display = "none";
      try {
        localStorage.setItem("fidelidapp-topbar-closed-until", String(Date.now() + 7 * 24 * 60 * 60 * 1000));
      } catch (err) {
        try { sessionStorage.setItem("fidelidapp-topbar-closed", "1"); } catch (err2) { }
      }
    });

    // Cierre por sesi\u00F3n (fallback cuando localStorage est\u00E1 bloqueado)
    try {
      if (sessionStorage.getItem("fidelidapp-topbar-closed") === "1") {
        topbar.style.display = "none";
      }
    } catch (err) { }

    // Si el cliente esta logueado, obtener sus puntos y actualizar el texto
    if (cfg && cfg.email) {
      apiGet(
        "/api/shopify/public/rewards?shop=" + encodeURIComponent(cfg.shop) +
        "&email=" + encodeURIComponent(cfg.email)
      ).then(function (data) {
        var points = data.points || 0;
        var rewards = data.rewards || [];
        var textEl = topbar.querySelector("[data-fidelidapp-topbar-text]");
        if (!textEl) return;

        // Canjeables + canje más cercano (para la micro-barra de progreso)
        var redeemable = 0;
        var nextReward = null;
        for (var i = 0; i < rewards.length; i++) {
          var r = rewards[i];
          var rPts = Number(r.points) || 0;
          if (r.available === false || rPts <= 0) continue;
          if (r.canRedeem) redeemable++;
          else if (!nextReward || rPts < (Number(nextReward.points) || 0)) nextReward = r;
        }

        if (redeemable > 0) {
          // Estado: puede canjear — único estado con color de acento
          topbar.classList.add("fidelidapp-topbar--highlight");
          textEl.innerHTML = redeemable === 1
            ? 'Tenés <strong>una recompensa</strong> para canjear — <strong>Canjeala</strong>'
            : 'Tenés <strong>' + redeemable + ' recompensas</strong> para canjear — <strong>Canjealas</strong>';
        } else if (nextReward) {
          // Estado: juntando (incluso con 0 pts) — saldo + barra de progreso +
          // meta con nombre + CTA. En mobile el CSS oculta la meta y deja
          // saldo + barra + CTA.
          var nPts = Number(nextReward.points) || 0;
          var pct = nPts > 0 ? Math.min(100, Math.round((points / nPts) * 100)) : 0;
          var missing = Math.max(0, nPts - points);
          textEl.innerHTML =
            '<strong class="fidelidapp-topbar__pts">' + points.toLocaleString() + ' pts</strong>' +
            '<span class="fidelidapp-topbar__progress" aria-hidden="true">' +
            '<span class="fidelidapp-topbar__progress-fill" style="width:' + pct + '%"></span>' +
            '</span>' +
            '<span class="fidelidapp-topbar__goal">Te faltan <strong>' + missing.toLocaleString() +
            ' pts</strong> para ' + escapeHtml(nextReward.description || 'tu próxima recompensa') + '</span>' +
            '<strong class="fidelidapp-topbar__cta">Ver recompensas</strong>';
        } else if (points > 0) {
          textEl.innerHTML =
            'Tenés <strong>' + points.toLocaleString() + ' puntos</strong> — ' +
            '<strong>Mirá las recompensas</strong>';
        } else {
          // Sin recompensas configuradas: copy genérico del programa
          textEl.innerHTML =
            'Empezá a sumar puntos con tus compras — ' +
            '<strong>Conocé el programa</strong>';
        }
      }).catch(function () {
        // Si falla, se queda con el texto por defecto
      });
    }
  }

  function initRewardsHeaderLink(url) {
    // No duplicar
    if (document.querySelector("[data-fidelidapp-nav-link]")) return;

    // Buscar el nav principal del header
    var navSelectors = [
      "header nav ul",
      "header .header__inline-menu ul",
      ".header__menu ul",
      ".site-nav",
      "nav.main-nav ul",
      ".navigation ul",
      "#SiteNav",
      ".header__navigation ul",
      "#AccessibleNav",
      "header .menu ul",
      ".header-nav ul",
    ];

    for (var i = 0; i < navSelectors.length; i++) {
      var nav = document.querySelector(navSelectors[i]);
      if (!nav) continue;

      // Verificar que no sea nav de footer o sidebar
      if (nav.closest("footer, .footer, .sidebar, .drawer")) continue;

      // Verificar que tenga al menos 2 items (es un menu real)
      var items = nav.querySelectorAll("li");
      if (items.length < 2) continue;

      // Clonar la estructura del ultimo item para mantener consistencia de estilos
      var lastItem = items[items.length - 1];
      var newItem = lastItem.cloneNode(true);

      // Limpiar el contenido clonado y reemplazar con nuestro link
      var existingLink = newItem.querySelector("a");
      if (existingLink) {
        existingLink.href = url;
        existingLink.textContent = "Recompensas";
        existingLink.setAttribute("data-fidelidapp-nav-link", "true");
        // Limpiar submenus clonados
        var submenus = newItem.querySelectorAll("ul, .submenu, .dropdown, .mega-menu");
        for (var s = 0; s < submenus.length; s++) {
          submenus[s].remove();
        }
      } else {
        // Si no hay link, crear uno
        newItem.innerHTML = '<a href="' + url + '" data-fidelidapp-nav-link="true">Recompensas</a>';
      }

      // Eliminar clases de dropdown del <li> clonado para no heredar comportamiento
      // de hover/JS del \u00EDtem original (has-dropdown, has-submenu, etc.)
      var dropdownClasses = Array.from(newItem.classList).filter(function (c) {
        return /dropdown|submenu|has-child|has-sub|mega|flyout/i.test(c);
      });
      dropdownClasses.forEach(function (c) { newItem.classList.remove(c); });

      nav.appendChild(newItem);
      return; // Solo inyectar en el primer nav encontrado
    }
  }

  // ════════════════════════════════════════════════════
  // 7. THANK-YOU PAGE: banner celebratorio post-compra
  // ════════════════════════════════════════════════════

  async function initThankYou() {
    var cfg = getConfig();
    if (!cfg) return;
    if (cfg.pageType !== "thank_you") return;

    // Contenedor principal del thank-you
    var mainContent = document.querySelector(
      '#MainContent, main, .thank-you, .os-step, [data-step="thank_you"], .content'
    );

    if (!mainContent) return;

    // No duplicar
    if (document.querySelector("[data-fidelidapp-thankyou]")) return;

    var banner = document.createElement("div");
    banner.className = "fidelidapp-thankyou notranslate";
    banner.setAttribute("translate", "no");
    banner.setAttribute("data-fidelidapp-thankyou", "true");
    banner.setAttribute("data-fidelidapp-injected", "true");

    if (!cfg.email) {
      // Cliente no logueado — invitar a crear cuenta
      banner.innerHTML =
        '<div class="fidelidapp-thankyou__content">' +
        '<span class="fidelidapp-thankyou__icon">' + FW_ICONS.star + '</span>' +
        '<div class="fidelidapp-thankyou__text">' +
        '<strong>Mir\u00E1 los puntos que ganaste con esta compra</strong>' +
        '<span class="fidelidapp-thankyou__sub">Ingres\u00E1 con tu email \u2014 sin contrase\u00F1a, te llega un c\u00F3digo</span>' +
        '</div>' +
        '<a href="' + escapeHtml(getLoginUrl()) + '" class="fidelidapp-thankyou__cta">Ver mis puntos</a>' +
        '</div>';
      mainContent.insertBefore(banner, mainContent.firstChild);
      return;
    }

    // Cliente logueado — obtener puntos
    try {
      var data = await apiGet(
        "/api/shopify/public/rewards?shop=" + encodeURIComponent(cfg.shop) +
        "&email=" + encodeURIComponent(cfg.email)
      );

      var points = data.points || 0;
      var rewards = data.rewards || [];

      // Encontrar la siguiente recompensa alcanzable o la mas cercana
      var redeemable = [];
      var nextReward = null;
      for (var i = 0; i < rewards.length; i++) {
        if (rewards[i].canRedeem) {
          redeemable.push(rewards[i]);
        } else if (!nextReward || rewards[i].points < nextReward.points) {
          nextReward = rewards[i];
        }
      }

      var innerHtml =
        '<div class="fidelidapp-thankyou__content">' +
        '<span class="fidelidapp-thankyou__icon">' + FW_ICONS.gift + '</span>' +
        '<div class="fidelidapp-thankyou__text">' +
        '<strong>\u00A1Ganaste puntos con esta compra!</strong>' +
        '<span class="fidelidapp-thankyou__sub">Ya tienes <strong>' + points.toLocaleString() + ' puntos</strong> en total</span>' +
        '</div>' +
        '</div>';

      if (redeemable.length > 0) {
        innerHtml += '<div class="fidelidapp-thankyou__reward">' +
          '<span>\u00A1Puedes canjear: <strong>' + escapeHtml(redeemable[0].description) + '</strong>!</span>' +
          '<a href="/pages/' + escapeHtml(cfg.rewardsPageSlug || "recompensas") + '" class="fidelidapp-thankyou__cta">Canjear recompensa</a>' +
          '</div>';
      } else if (nextReward) {
        var pct = Math.min(100, Math.round((points / nextReward.points) * 100));
        var missing = nextReward.points - points;
        innerHtml += '<div class="fidelidapp-thankyou__progress">' +
          '<span class="fidelidapp-thankyou__progress-label">Próxima recompensa: ' + escapeHtml(nextReward.description) + ' — faltan ' + missing.toLocaleString() + ' pts</span>' +
          '<div class="fidelidapp-thankyou__progress-bar"><div class="fidelidapp-thankyou__progress-fill" style="width:' + pct + '%"></div></div>' +
          '</div>';
      }

      banner.innerHTML = innerHtml;
      mainContent.insertBefore(banner, mainContent.firstChild);
    } catch (e) {
      console.warn("[FidelidApp] Error en thank-you page:", e);
    }
  }

  // ════════════════════════════════════════════════════
  // 8. TOAST: notificacion de puntos nuevos
  // ════════════════════════════════════════════════════

  async function initPointsToast() {
    var cfg = getConfig();
    if (!cfg || !cfg.email) return;
    // No mostrar en thank-you (ya tiene su banner)
    if (cfg.pageType === "thank_you") return;

    try {
      var data = await getPointsData(cfg);

      var currentPoints = data.points || 0;
      var lastPointsStr = null;
      try { lastPointsStr = sessionStorage.getItem("fidelidapp-last-points"); } catch (e) { }

      // Guardar puntos actuales
      try { sessionStorage.setItem("fidelidapp-last-points", currentPoints.toString()); } catch (e) { }

      if (lastPointsStr !== null) {
        var lastPoints = parseInt(lastPointsStr, 10);
        if (currentPoints > lastPoints) {
          var diff = currentPoints - lastPoints;
          showPointsToast(diff, currentPoints);
        }
      }
    } catch (e) {
      // Silenciar errores
    }
  }

  function showPointsToast(diff, total) {
    // No duplicar
    if (document.querySelector("[data-fidelidapp-toast]")) return;

    var toast = document.createElement("div");
    toast.className = "fidelidapp-toast";
    toast.setAttribute("data-fidelidapp-toast", "true");
    toast.innerHTML =
      '<div class="fidelidapp-toast__content">' +
      '<span class="fidelidapp-toast__icon">' + FW_ICONS.star + '</span>' +
      '<div class="fidelidapp-toast__text">' +
      '<strong>\u00A1Sumaste ' + diff.toLocaleString() + ' puntos!</strong>' +
      '<span>Total: ' + total.toLocaleString() + ' puntos</span>' +
      '</div>' +
      '<button class="fidelidapp-toast__close" aria-label="Cerrar">\u2715</button>' +
      '</div>';

    document.body.appendChild(toast);

    // Animar entrada
    requestAnimationFrame(function () {
      toast.classList.add("fidelidapp-toast--visible");
    });

    // Cerrar al click
    toast.querySelector(".fidelidapp-toast__close").addEventListener("click", function () {
      dismissToast(toast);
    });

    // Auto-cerrar en 8 segundos
    setTimeout(function () {
      dismissToast(toast);
    }, 8000);
  }

  function showActionToast(message, type) {
    // type: 'success' | 'error'
    var toast = document.createElement('div');
    toast.className = 'fidelidapp-toast fidelidapp-toast--action fidelidapp-toast--' + (type || 'success');
    toast.innerHTML =
      '<div class="fidelidapp-toast__content">' +
      '<span class="fidelidapp-toast__icon">' + (type === 'error' ? FW_ICONS.alert : FW_ICONS.check) + '</span>' +
      '<div class="fidelidapp-toast__text"><strong></strong></div>' +
      '</div>';
    toast.querySelector('strong').textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(function () {
      toast.classList.add('fidelidapp-toast--visible');
    });
    setTimeout(function () {
      dismissToast(toast);
    }, 4000);
  }

  function dismissToast(toast) {
    if (!toast || !toast.parentNode) return;
    toast.classList.remove("fidelidapp-toast--visible");
    setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 400);
  }

  // ════════════════════════════════════════════════════
  // 9. HEADER BADGE: puntos en el header
  // ════════════════════════════════════════════════════

  async function initHeaderBadge() {
    var cfg = getConfig();
    if (!cfg || !cfg.email) return;
    // No duplicar
    if (document.querySelector("[data-fidelidapp-header-badge]")) return;

    try {
      var data = await getPointsData(cfg);

      var points = data.points || 0;
      if (points <= 0) return;

      // Buscar area de iconos del header (junto a carrito/cuenta)
      var headerTargets = [
        ".header__icons",
        ".header__icon-list",
        ".header-icons",
        ".site-header__icons",
        ".header__buttons",
        "header .header__heading-link ~ *",
        "header nav + div",
        "header .utility-nav",
        ".header__actions",
      ];

      var headerArea = null;
      for (var i = 0; i < headerTargets.length; i++) {
        headerArea = document.querySelector(headerTargets[i]);
        if (headerArea) break;
      }

      if (!headerArea) return;

      var rewardsUrl = "/pages/" + (cfg.rewardsPageSlug || "recompensas");

      var badge = document.createElement("a");
      badge.href = rewardsUrl;
      badge.className = "fidelidapp-header-badge";
      badge.setAttribute("data-fidelidapp-header-badge", "true");
      badge.innerHTML = FW_ICONS.star + '<span>' + points.toLocaleString() + ' pts</span>';

      // Insertar al inicio del area de iconos
      headerArea.insertBefore(badge, headerArea.firstChild);
    } catch (e) {
      // Silenciar errores
    }
  }

  // ─── Init ───

  function init() {
    if (window.__fidelidappInitialized) return;
    window.__fidelidappInitialized = true;
    if (!getConfig()) return;
    applyThemeTokens();

    // Navigation links siempre (excepto en la pagina de recompensas misma)
    initRewardsNavigation();

    // Thank-you page
    if (getConfig().pageType === "thank_you") {
      initThankYou();
      initPointsToast();
      return;
    }

    // Pagina de recompensas tiene prioridad (no inyectar otros widgets ahi)
    if (isRewardsPage()) {
      initRewardsPage();
      return;
    }

    initProductPage();
    initComboPage();
    initProductCards();
    initCart();
    initAccount();

    // Toast y badge en cualquier pagina
    initPointsToast();
    initHeaderBadge();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
